import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { usePreloadChunk } from "@/lib/lazy-chunk";
import { CameraScanner } from "@/lib/scan-ui/lazy-camera";
import { CharacteristicChoice, charAnswer } from "@/lib/scan-ui/characteristic-choice";
import { PartiaAsk } from "@/lib/scan-ui/partia-ask";
import { LooseRemainder } from "@/lib/scan-ui/loose-remainder";
import { DUPLICATE_MARK_TEXT, sameMarkIn } from "@/lib/scan-ui/marks";
import {
  ArrowLeft,
  Camera,
  Check,
  CheckCircle2,
  ChevronRight,
  FileText,
  Loader2,
  MessageSquare,
  Plus,
  Printer,
  RotateCcw,
  ScanLine,
  X,
} from "lucide-react";
import React, { Suspense, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";

import {
  type ItemCreate,
  type NeedLine,
  type PalletSummary,
  type PlanConflict,
  type ResolvedScan,
  type SessionDetail,
  addBoxItems,
  addItem,
  addPallet,
  createOrderCheckSession,
  kindSuffix,
  deleteItem,
  deletePallet,
  downloadAllPalletSheets,
  printAllPalletLabels,
  reattachPalletSheets,
  downloadZyaReestr,
  finalizeOrderCheckSession,
  getOrderCheckConfig,
  getOrderCheckSession,
  getScanIndex,
  getSessionPlanReview,
  listPalletPhotos,
  lookupPartia,
  reloadPlan,
  reopenOrderCheckSession,
  resolveScan,
  stagePalletPhoto,
  updateItemQty,
} from "@/api/order_check";
import { useAuth } from "@/auth/useAuth";
import { PhotoCapture } from "@/components/PhotoCapture";
import { BoxPlanProgress, boxPlanDone } from "@/pages/order-check/v2/BoxPlanProgress";
import { CharacteristicBadge } from "@/pages/order-check/v2/CharacteristicBadge";
import { DeletedCharacteristicBanner } from "@/pages/order-check/v2/DeletedCharacteristicBanner";
import { EmptyPalletBadge, EmptyPalletsWarning, emptyPallets } from "@/pages/order-check/v2/EmptyPallet";
import { palletNumber } from "@/pages/order-check/v2/pallet-number";
import { PlanCheckChip, PlanCheckSheet } from "@/pages/order-check/v2/PlanCheck";
import { type PlanCheck, planCheckState, transferPlanCheckState } from "@/pages/order-check/v2/plan-check-state";
import { usePortalConfig } from "@/portal/usePortalConfig";
import { Button } from "@/components/ui/button";
import { blobErrorMessage, errorDetail, extractErrorMessage, scanErrorTitle } from "@/lib/api-errors";
import { fmtDate } from "@/lib/format";
import {
  BulkDeleteError,
  type NeedStatus,
  REMAINING_EPS,
  boxLineBody,
  buildNeedIndex,
  ck,
  compareNeedsForSheet,
  decideBox,
  deleteItemsInOrder,
  deletedCharacteristicLines,
  findNeed,
  granKey,
  needGranKey,
  notInOrderHero,
  needStatus,
  norm,
  palletsWord,
  plural,
  requiredBoxCoef,
  useOrderKind,
} from "@/lib/order-check";
import { type OrderScan, parseScan } from "@/lib/order-scan";
import { useScanCapture } from "@/lib/use-scan-capture";
import { cn } from "@/lib/utils";
import { type BlockedScan, BlockedScanBanner } from "@/pages/fbs/ScanBanner";
import { type AcceptedBox, BoxAcceptedCard } from "@/lib/scan-ui/box-accepted";
import { BottomSheet, Dialog } from "@/lib/scan-ui/overlays";
import { QtyInput } from "@/lib/scan-ui/qty-input";
import { resolveFromIndex, toResolveRequest } from "@/lib/scan-ui/scan-resolve";
import { STATUS_ROW, STATUS_TEXT, fmtQty } from "@/lib/scan-ui/status";
import { useUploadProgress } from "@/lib/use-upload-progress";

/** Скан + признак «код набран руками» (`ManualCodeSheet`): такая позиция уходит
 * с `scan_kind="manual"`, и журнал отгрузки отличает её от отсканированной. */
type CheckScan = OrderScan & { manual: boolean };

interface Draft {
  palletId: string;
  scan: CheckScan;
  resolved: ResolvedScan;
  charRef: string;
  charName: string;
  withoutChar: boolean;
  partiaRef: string;
  partiaName: string;
  partiaSource: string;
  withoutPartia: boolean;
  qty: number;
  mode: "char" | "partia" | "over" | "extra" | "normal";
}

type Hero =
  | { kind: "nopallets" }
  | { kind: "idle" }
  | { kind: "busy"; raw: string }
  | { kind: "ok"; item: ItemView2 }
  | { kind: "box"; box: AcceptedBox }
  | { kind: "error"; title: string; detail: string; raw: string; retry?: () => void }
  | { kind: "extra"; resolved: ResolvedScan; scan: CheckScan };

interface ItemView2 {
  article: string;
  name: string;
  char: string;
  partia: string;
  coef: number;
  added: number;
  need: NeedLine | null;
  itemId: string;
  /** SSCC коробки, если скан — коробка портала; такую количеством не правят. */
  sscc: string;
}

export function OrderCheckSessionPageV2(): React.ReactElement {
  // Чанк камеры тянем при ВХОДЕ: устаревший чанк после выката
  // перезагружает страницу, и делать это в момент тапа «Сканировать»
  // значит прерывать работу ровно посередине.
  usePreloadChunk(CameraScanner);
  const navigate = useNavigate();
  const { ref } = useParams<{ ref: string }>();
  // ⚠️ Вид — В URL, а не в состоянии экрана: заказ на перемещение читается из
  // ДРУГОГО документа 1С, и после перезагрузки страницы (её делает и обновление
  // ленивого чанка) портал искал бы его среди заказов покупателя и отвечал
  // «заказ не найден».
  const kind = useOrderKind();
  const sessionQ = useQuery({
    queryKey: ["ocv2-session", ref, kind],
    queryFn: () => createOrderCheckSession(ref!, kind),
    enabled: !!ref,
  });

  return (
    <div className="mx-auto flex h-app max-w-md flex-col bg-slate-50">
      {sessionQ.isLoading && (
        <div className="flex flex-1 items-center justify-center text-slate-400">
          <Loader2 className="size-7 animate-spin" />
        </div>
      )}
      {sessionQ.isError && (
        <div className="m-3 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
          Не удалось открыть проверку: {extractErrorMessage(sessionQ.error, "ошибка")}
          <Button variant="outline" size="sm" className="mt-2" onClick={() => navigate("/order-check")}>
            К списку
          </Button>
        </div>
      )}
      {sessionQ.data && (
        <SessionBody key={sessionQ.data.id} sessionId={sessionQ.data.id} />
      )}
    </div>
  );
}

function SessionBody({ sessionId }: { sessionId: string }): React.ReactElement {
  const kind = useOrderKind();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const detailKey = ["ocv2-detail", sessionId];
  const detailQ = useQuery({ queryKey: detailKey, queryFn: () => getOrderCheckSession(sessionId) });
  const refetch = () => qc.invalidateQueries({ queryKey: detailKey });

  const configQ = useQuery({ queryKey: ["oc-config"], queryFn: getOrderCheckConfig, staleTime: 300000 });
  const photoRequired = configQ.data?.pallet_photo_required ?? true;
  const scanMethod = configQ.data?.scan_method ?? "tsd";

  const scanIndexQ = useQuery({
    queryKey: ["ocv2-scan-index", sessionId],
    queryFn: () => getScanIndex(sessionId),
    staleTime: 3600000,
    gcTime: 3600000,
    refetchOnMount: "always",
    refetchOnWindowFocus: false,
  });
  const photosQ = useQuery({ queryKey: ["ocv2-photos", sessionId], queryFn: () => listPalletPhotos(sessionId), staleTime: 30000 });

  const detail = detailQ.data;
  // Сверка раскладки (план паллет портала) с заказом 1С — при открытии сессии.
  // Только у заказа покупателя: перемещениям раскладку пока никто не строит
  // (ручка её сверить умеет с этапа 3в), и завершённой сборке она ни к чему.
  const planReviewKey = ["ocv2-plan-review", sessionId];
  const planReviewQ = useQuery({
    queryKey: planReviewKey,
    queryFn: () => getSessionPlanReview(sessionId),
    enabled: kind === "customer" && !!detail && detail.header.status !== "completed",
    // Возврат со страницы паллеты не должен снова гонять 1С и мигать значком.
    // «Перепроверить» (`refetch`) и «Обновить план» (`invalidate`) — запрос
    // заново всё равно.
    staleTime: 60_000,
  });
  const { user } = useAuth();
  const { hasPage } = usePortalConfig();
  const canOpenPlan =
    hasPage("order_plan") && !!user && (user.is_admin || user.permissions.includes("order_plan"));
  const pallets = useMemo(() => detail?.pallets ?? [], [detail]);
  const [activePallet, setActivePallet] = useState("");
  const [hero, setHero] = useState<Hero>({ kind: "idle" });
  const [draft, setDraft] = useState<Draft | null>(null);
  const [sheet, setSheet] = useState<SheetState>(null);
  const [dialog, setDialog] = useState<null | "fin" | "reopen" | "clear">(null);
  const [cameraScan, setCameraScan] = useState(false);
  const [manualOpen, setManualOpen] = useState(false);
  const [photoModal, setPhotoModal] = useState<null | { palletId: string; label: string; then: "new" | "finalize" }>(null);
  // Очередь паллет, которые осталось сфотографировать перед завершением.
  const [photoQueue, setPhotoQueue] = useState<PalletSummary[]>([]);
  const [blocked, setBlocked] = useState<BlockedScan | null>(null);

  const activeP = pallets.find((p) => p.id === activePallet);
  const activeHasItems = (activeP?.items_count ?? 0) > 0;
  // Паллеты, в которые эта сессия вправе писать: отгруженная, аннулированная,
  // взятая новой проверкой (`writable: false`) — только смотреть.
  const writablePallets = useMemo(() => pallets.filter((p) => p.writable), [pallets]);

  const photosByPallet = useMemo(() => {
    const m: Record<string, number> = {};
    for (const ph of photosQ.data ?? []) m[ph.pallet_id] = (m[ph.pallet_id] ?? 0) + 1;
    return m;
  }, [photosQ.data]);

  // Держим активную паллету валидной: по умолчанию — последняя ИЗМЕНЯЕМАЯ.
  // Нередактируемую активной не делаем никогда (скан на неё бэкенд отобьёт);
  // изменяемых нет — активной нет, первый скан/«Новая паллета» заведёт свою.
  useEffect(() => {
    if (writablePallets.some((p) => p.id === activePallet)) return;
    const last = writablePallets[writablePallets.length - 1]?.id ?? "";
    if (last !== activePallet) setActivePallet(last);
  }, [writablePallets, activePallet]);

  useEffect(() => {
    if (!pallets.length) setHero({ kind: "nopallets" });
    else if (hero.kind === "nopallets") setHero({ kind: "idle" });
  }, [pallets.length]); // eslint-disable-line react-hooks/exhaustive-deps

  const busy = hero.kind === "busy";
  // scanEnabled — только для ХУКА аппаратного перехвата (пауза при оверлеях).
  // Обработку скана (handleScan) НЕ гейтим им: камера/ручной ввод закрывают свой
  // оверлей и тут же зовут handleScan — в этот момент cameraScan в замыкании ещё
  // true, и скан молча терялся. Реентрантность держим через ref.
  const scanEnabled =
    detail?.header.status === "open" && !draft && !sheet && !dialog && !photoModal && !cameraScan && !busy;
  const resolvingRef = useRef(false);

  const createPalletM = useMutation({
    mutationFn: () => addPallet(sessionId),
    onSuccess: (p) => {
      setActivePallet(p.id);
      refetch();
    },
    onError: (e) => toast.error(`Паллета: ${extractErrorMessage(e, "ошибка")}`),
  });

  // ── обработка скана ────────────────────────────────────────────────────
  async function ensurePallet(): Promise<string | null> {
    if (activePallet) return activePallet;
    const last = writablePallets[writablePallets.length - 1];
    if (last) return last.id;
    const p = await addPallet(sessionId);
    setActivePallet(p.id);
    await refetch();
    return p.id;
  }

  async function handleScan(raw: string, manual = false) {
    if (detail?.header.status !== "open" || draft || sheet || dialog || photoModal) return;
    if (resolvingRef.current) {
      navigator.vibrate?.([60, 60, 60]);
      return;
    }
    resolvingRef.current = true;
    setBlocked(null);
    try {
      await processScan(raw, manual);
    } finally {
      resolvingRef.current = false;
    }
  }

  async function processScan(raw: string, manual: boolean) {
    const scan: CheckScan = { ...parseScan(raw), manual };
    const req = toResolveRequest(scan);
    if (!req) {
      navigator.vibrate?.([60, 60, 60]);
      setHero({ kind: "error", title: "Не распознан код", detail: "Код не разобран как ШК/GS1/ЧЗ.", raw: raw.slice(0, 24) });
      return;
    }
    // Та же штука ЧЗ второй раз в сессию — отказ сразу, без 1С (бэк — 409).
    if (sameMarkIn(scan, pallets.flatMap((p) => p.items))) {
      setHero({ kind: "error", title: "Уже отсканировано", detail: DUPLICATE_MARK_TEXT, raw });
      navigator.vibrate?.([60, 60, 60]);
      return;
    }
    const palletId = await ensurePallet();
    if (!palletId) return;

    const local = resolveFromIndex(scan, scanIndexQ.data);
    let resolved = local;
    if (!resolved) {
      setHero({ kind: "busy", raw });
      try {
        resolved = await resolveScan(sessionId, req);
      } catch (e) {
        setHero({
          kind: "error",
          // Состав коробки читается из БД портала, 1С тут ни при чём.
          title: scan.kind === "sscc" ? "Коробку не принять" : scanErrorTitle(e, "Ошибка 1С"),
          detail:
            scan.kind === "sscc"
              ? `Не удалось получить состав коробки: ${extractErrorMessage(e, "сервер не ответил")}`
              : extractErrorMessage(e, "Не удалось связаться с 1С"),
          raw,
          retry: () => handleScan(raw, manual),
        });
        navigator.vibrate?.([60, 60, 60]);
        return;
      }
    }
    if (!resolved.matched) {
      // Коробку не опознали или не берём (паллета упаковки, отменённая, пустая)
      // — «товар не найден в 1С» про неё неправда: 1С её даже не спрашивали.
      const title = resolved.scan_kind === "sscc" ? "Коробку не принять" : "Товар не найден в 1С";
      setHero({ kind: "error", title, detail: resolved.message || "Нет в 1С", raw });
      navigator.vibrate?.([60, 60, 60]);
      return;
    }
    // Вскрытый короб упаковки: принимается ОСТАТОК — сказать об этом сразу.
    if (resolved.notice) toast.warning(resolved.notice, { duration: 10000 });
    if (resolved.box_lines?.length) {
      await acceptBox(scan, resolved, palletId);
      return;
    }
    decideAfterResolve(scan, resolved, palletId);
  }

  /** Сборная коробка — ЦЕЛИКОМ или никак (решение 22.10.2026): каждая строка
   *  проходит правила одиночного скана (`decideBox`), иначе отказ построчно и
   *  ничего не отправлено; прошли все — одна запись `…/items/box`. */
  async function acceptBox(scan: CheckScan, resolved: ResolvedScan, palletId: string) {
    // Сборная коробка без номера — ответ сломан: без SSCC ни повтор не узнать,
    // ни коробку не назвать. Молча подставлять пустой нельзя.
    const sscc = resolved.sscc;
    if (!sscc) {
      setHero({
        kind: "error",
        title: "Коробку не принять",
        detail: "В ответе сервера нет номера коробки (SSCC) — коробка не принята. Сообщите администратору.",
        raw: scan.raw,
      });
      navigator.vibrate?.([60, 60, 60]);
      return;
    }
    const lines = resolved.box_lines ?? [];
    const verdict = decideBox(detail?.needs ?? [], lines, { doc: "order" }, {
      sscc,
      items: pallets.flatMap((p) => p.items),
    });
    if (!verdict.ok) {
      setHero({ kind: "error", title: "Коробку не принять", detail: verdict.text, raw: scan.raw });
      navigator.vibrate?.([60, 60, 60]);
      return;
    }
    const scanKind = scan.manual ? "manual" : resolved.scan_kind;
    try {
      await addBoxItems(sessionId, {
        pallet_id: palletId,
        raw: scan.raw,
        items: lines.map((l) => ({ ...boxLineBody(l, scan, scanKind), pallet_id: palletId, qty_manual: false })),
      });
    } catch (e) {
      // Директивный режим: отказ по плану — героем с его заголовком, как у
      // одиночного скана; прочий отказ (состав, повтор коробки) — тоже героем:
      // коробка не легла, и человек должен прочитать почему.
      const plan = errorDetail<PlanErrorDetail>(e);
      setHero({
        kind: "error",
        title: plan ? (PLAN_ERROR_TITLE[plan.code] ?? "Не по плану") : "Коробку не принять",
        detail: plan ? plan.detail : extractErrorMessage(e, "Не удалось записать коробку"),
        raw: scan.raw,
      });
      navigator.vibrate?.([60, 60, 60]);
      return;
    }
    navigator.vibrate?.(40);
    setHero({
      kind: "box",
      box: {
        sscc,
        lines: lines.map((l) => ({
          article: l.nomenclature_article,
          name: l.nomenclature_name,
          characteristic: l.characteristic_name,
          partia: l.partia_name,
          qty: l.qty_base,
          unit: "шт",
        })),
      },
    });
    // ⚠️ Ждём ВНУТРИ замка скана (`resolvingRef`): следующая коробка, пойманная
    // до обновления, решалась бы по старому «собрано» — и перебор проходил.
    await refetch();
  }

  function decideAfterResolve(scan: CheckScan, resolved: ResolvedScan, palletId: string) {
    const needs = detail?.needs ?? [];
    const nom = resolved.nomenclature_ref;
    const needsForNom = needs.filter((n) => n.nomenclature_ref === nom);
    if (needsForNom.length === 0) {
      // номенклатуры нет в заказе — «нет в заказе»
      setHero({ kind: "extra", resolved, scan });
      navigator.vibrate?.([60, 60, 60]);
      // авто-возврат к idle через 4с
      window.setTimeout(() => setHero((h) => (h.kind === "extra" ? { kind: "idle" } : h)), 4000);
      return;
    }
    const base = baseDraft(resolved, scan, palletId, "normal");
    if (resolved.needs_characteristic && !base.charRef) {
      setDraft({ ...base, mode: "char" });
      return;
    }
    if (resolved.needs_partia && !base.partiaRef) {
      setDraft({ ...base, mode: "partia" });
      return;
    }
    const scanPartia = base.withoutPartia ? "" : base.partiaRef;
    const scanCoef = resolved.unit_coefficient || 1;
    // перебор/учёт — по конкретной (ном, хар, ПАРТИЯ, ЕДИНИЦА)
    const need = findNeed(needs, nom, base.charRef, scanPartia, scanCoef);
    // «Не та единица»: заказ требует короба, а код не той упаковки (штучный/
    // россыпь/чужой размер) — блокируем скан (короба грузим строго коробами).
    const boxCoef = requiredBoxCoef(needs, nom, base.charRef);
    if (!need && boxCoef > 1 && ck(scanCoef) !== ck(boxCoef)) {
      setHero({
        kind: "error",
        title: `Нужны короба ×${fmtQty(boxCoef)}`,
        detail:
          scanCoef > 1
            ? `Отсканирован короб ×${fmtQty(scanCoef)} — не тот размер упаковки.`
            : "Отсканирован штучный код. Эта позиция заказа — в коробах.",
        raw: scan.raw,
      });
      navigator.vibrate?.([60, 60, 60]);
      return;
    }
    // ⚠️ Потребность не нашлась, а единица подошла — значит расходится ПАРТИЯ
    // или ХАРАКТЕРИСТИКА. Раньше отсюда шёл авто-учёт с зелёной карточкой, и
    // кладовщик узнавал о расхождении только на «Завершить», когда паллета уже
    // сложена. Про единицу проверка была, про эти две — нет.
    if (!need) {
      // Одинаковые названия при разных ссылках (в т.ч. помеченная на удаление
      // характеристика строки заказа) — объясняет сам текст. «Не из заказа»
      // здесь недостижимо (номенклатуру отсекли выше), но молчать в нём нельзя.
      const why = notInOrderHero(
        needs, nom, { ref: base.charRef, name: base.charName },
        base.withoutPartia ? "" : base.partiaName, "order",
      );
      setHero({ kind: "error", ...why, raw: scan.raw });
      navigator.vibrate?.([60, 60, 60]);
      return;
    }
    if (need.qty_scanned + base.qty > need.qty_needed + REMAINING_EPS) {
      setDraft({ ...base, mode: "over" });
      return;
    }
    // авто-учёт
    void addResolved(base);
  }

  async function addResolved(d: Draft, opts?: { loose?: boolean }) {
    let palletId = d.palletId;
    if (!pallets.some((p) => p.id === palletId)) {
      const ensured = await ensurePallet();
      if (!ensured) {
        toast.error("Паллета сканирования удалена — пересканируйте");
        return;
      }
      // Черновик без паллеты: изменяемой активной не было (все отгружены и
      // т.п.) — скан ложится на только что заведённую свою.
      if (!palletId) palletId = ensured;
    }
    const r = d.resolved;
    const loose = opts?.loose ?? false;
    const body: ItemCreate = {
      pallet_id: palletId,
      scan_kind: d.scan.manual ? "manual" : r.scan_kind,
      raw: d.scan.raw,
      ean13: d.scan.ean13 ?? null,
      gtin: d.scan.gtin ?? null,
      serial: d.scan.serial ?? null,
      nomenclature_ref: r.nomenclature_ref,
      nomenclature_name: r.nomenclature_name,
      nomenclature_article: r.nomenclature_article,
      characteristic_ref: d.withoutChar ? "" : d.charRef,
      characteristic_name: d.withoutChar ? "" : d.charName,
      partia_ref: d.withoutPartia ? "" : d.partiaRef,
      partia_name: d.withoutPartia ? "" : d.partiaName,
      partia_source: !d.withoutPartia && d.partiaRef ? d.partiaSource || "manual" : "none",
      unit_ref: r.unit_ref,
      unit_coefficient: loose ? 1 : r.unit_coefficient,
      qty_scanned: loose ? d.qty : r.qty_scanned,
      qty_base: d.qty,
      // Количество не то, что принёс код, — его задал человек.
      qty_manual: d.qty !== (r.qty_base || 1),
    };
    try {
      const created = await addItem(sessionId, body);
      navigator.vibrate?.(40);
      const need = findNeed(
        detail?.needs ?? [],
        r.nomenclature_ref,
        d.withoutChar ? "" : d.charRef,
        d.withoutPartia ? "" : d.partiaRef,
        loose ? 1 : r.unit_coefficient,
      );
      setHero({
        kind: "ok",
        item: {
          article: r.nomenclature_article,
          name: r.nomenclature_name,
          char: d.withoutChar ? "" : d.charName,
          partia: d.withoutPartia ? "" : d.partiaName,
          coef: loose ? 1 : r.unit_coefficient,
          added: d.qty,
          need,
          itemId: created.id,
          sscc: created.sscc ?? "",
        },
      });
      setDraft(null);
      refetch();
    } catch (e) {
      // Директивный режим: отказ по плану — это не «ошибка добавления», а
      // указание кладовщику, что делать. Показываем героем, а не тостом:
      // тост исчезает, а решение принимать надо.
      const detailBody = errorDetail<PlanErrorDetail>(e);
      if (detailBody) {
        setHero({
          kind: "error",
          title: PLAN_ERROR_TITLE[detailBody.code] ?? "Не по плану",
          detail: detailBody.detail,
          raw: "",
        });
        setDraft(null);
        return;
      }
      toast.error(`Добавление: ${extractErrorMessage(e, "ошибка")}`);
    }
  }

  // «Обновить план» — единственный предусмотренный выход, когда товара не
  // хватает: старший склада правит заказ в 1С, кладовщик перечитывает план.
  const reloadPlanM = useMutation({
    mutationFn: () => reloadPlan(sessionId),
    onSuccess: (data) => {
      qc.setQueryData(["ocv2-detail", sessionId], data);
      if (kind === "customer") void qc.invalidateQueries({ queryKey: planReviewKey });
      toast.success(
        data.plan_conflicts.length
          ? "План обновлён, но есть расхождения — проверьте паллеты"
          : "План обновлён",
      );
    },
    onError: (e) => toast.error(`План: ${extractErrorMessage(e, "ошибка")}`),
  });

  const undoM = useMutation({
    mutationFn: (itemId: string) => deleteItem(sessionId, itemId),
    onSuccess: () => {
      toast.success("Отменено");
      setHero({ kind: "idle" });
      refetch();
    },
    onError: (e) => toast.error(`Отмена: ${extractErrorMessage(e, "ошибка")}`),
  });
  const setQtyM = useMutation({
    mutationFn: (v: { itemId: string; count: number; coef: number }) =>
      updateItemQty(sessionId, v.itemId, { qty_scanned: v.count, qty_base: v.count * v.coef }),
    onSuccess: () => refetch(),
    onError: (e) => toast.error(`Количество: ${extractErrorMessage(e, "ошибка")}`),
  });
  const removeItemM = useMutation({
    mutationFn: (itemId: string) => deleteItem(sessionId, itemId),
    onSuccess: () => {
      toast.success("Позиция удалена");
      refetch();
    },
    onError: (e) => toast.error(`Удаление: ${extractErrorMessage(e, "ошибка")}`),
  });
  // «Удалить все» лишние — по одной, пропуская строки, которые сервер уже снял
  // вместе с коробкой (`deleteItemsInOrder`); сбой — тост с числом снятого.
  const removeItemsM = useMutation({
    mutationFn: (ids: string[]) => deleteItemsInOrder(ids, (id) => deleteItem(sessionId, id)),
    onSuccess: (removed) => toast.success(`Удалено ${removed} ${plural(removed, ["скан", "скана", "сканов"])}`),
    onError: (e) => {
      const done = e instanceof BulkDeleteError ? e.removed : 0;
      const cause = e instanceof BulkDeleteError ? e.cause : e;
      toast.error(`Удалено ${done}, дальше ошибка: ${extractErrorMessage(cause, "")}`);
    },
    onSettled: () => refetch(),
  });

  // ── фото / новая паллета ────────────────────────────────────────────────
  function requestNewPallet() {
    // Активной нет (паллет нет вовсе или ни одна не изменяема) — новая без
    // условий: фото и «сначала отсканируйте» касаются только СВОЕЙ текущей.
    if (!activeP?.writable) {
      createPalletM.mutate();
      return;
    }
    if (!activeHasItems) {
      toast.error("Сначала отсканируйте товары на текущую паллету");
      return;
    }
    if (photoRequired && (photosByPallet[activePallet] ?? 0) === 0) {
      setPhotoModal({ palletId: activePallet, label: activeP?.label ?? "паллету", then: "new" });
    } else {
      createPalletM.mutate();
    }
  }

  // Процент отправки снимка — общий хук (см. `use-upload-progress`).
  const photoProgress = useUploadProgress();
  const stagePhotoM = useMutation({
    mutationFn: (v: { palletId: string; blob: Blob }) =>
      stagePalletPhoto(sessionId, v.palletId, v.blob, photoProgress.start()),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["ocv2-photos", sessionId] });
      toast.success("Фото сохранено в черновик");
    },
    onError: (e) => toast.error(`Фото: ${extractErrorMessage(e, "ошибка")}`),
    onSettled: photoProgress.reset,
  });

  /** Непустые паллеты без фото — снимок нужен на КАЖДУЮ, а не на последнюю:
   *  фото это единственное доказательство укладки, когда клиент заявляет бой.
   *  Нередактируемую (отгружена и т.п.) не просим: фото на неё бэкенд не примет. */
  function palletsMissingPhoto(): PalletSummary[] {
    if (!photoRequired) return [];
    return writablePallets.filter((p) => p.items_count > 0 && (photosByPallet[p.id] ?? 0) === 0);
  }

  function askPhoto(queue: PalletSummary[]) {
    const next = queue[0];
    if (!next) return;
    setPhotoQueue(queue.slice(1));
    setPhotoModal({ palletId: next.id, label: next.label ?? "паллету", then: "finalize" });
  }

  function requestFinalize() {
    const missing = palletsMissingPhoto();
    if (missing.length) {
      setDialog(null);
      askPhoto(missing);
    } else {
      finalizeM.mutate();
    }
  }

  async function onPhotoCaptured(blob: Blob) {
    if (!photoModal) return;
    await stagePhotoM.mutateAsync({ palletId: photoModal.palletId, blob });
    const then = photoModal.then;
    setPhotoModal(null);
    if (then === "new") {
      createPalletM.mutate();
      return;
    }
    // Ещё не всё снято — идём к следующей паллете, не завершая.
    if (photoQueue.length) askPhoto(photoQueue);
    else finalizeM.mutate();
  }

  const finalizeM = useMutation({
    mutationFn: () => finalizeOrderCheckSession(sessionId),
    onSuccess: (r) => {
      toast.success(`Создана расходная ${r.expense_number}`);
      if (r.attach_warning) toast.warning(r.attach_warning, { duration: 8000 });
      // УПД нужен на отгрузке, а не сейчас, — но молчать нельзя: иначе
      // экран показывает штатное «завершено», а документа нет.
      if (r.invoice_warning) toast.warning(r.invoice_warning, { duration: 8000 });
      setDialog(null);
      refetch();
    },
    onError: (e) => toast.error(`Завершение: ${extractErrorMessage(e, "ошибка")}`),
  });

  const reopenM = useMutation({
    mutationFn: (clear: boolean) => reopenOrderCheckSession(sessionId, clear),
    onSuccess: () => {
      setDialog(null);
      refetch();
    },
    onError: (e) => toast.error(`Возврат: ${extractErrorMessage(e, "ошибка")}`),
  });
  // Очистка ОТКРЫТОГО заказа = удалить все паллеты (позиции каскадом).
  const clearM = useMutation({
    mutationFn: async () => {
      for (const p of pallets) await deletePallet(sessionId, p.id);
    },
    onSuccess: () => {
      toast.success("Заказ очищен");
      setDialog(null);
      setHero({ kind: "idle" });
      refetch();
    },
    onError: (e) => toast.error(`Очистка: ${extractErrorMessage(e, "ошибка")}`),
  });

  // ── глобальный перехват скана ───────────────────────────────────────────
  // Код пришёл, пока перехват выключен: не теряем молча — называем, почему
  // он не принят. Только открытая проверка: завершённая — чтение.
  function onBlocked(raw: string) {
    if (detail?.header.status !== "open") return;
    const reason = busy
      ? "Предыдущий скан ещё обрабатывается — отсканируйте ещё раз."
      : draft
        ? "Сначала ответьте на вопрос в открытом окне, потом сканируйте."
        : photoModal
          ? "Сначала сфотографируйте паллету или закройте окно фото."
          : cameraScan
            ? "Открыта камера — закройте её, чтобы сканировать сканером."
            : "Сначала закройте открытое окно.";
    setBlocked({ raw, reason });
  }
  const scan = useScanCapture({ enabled: scanEnabled, onScan: handleScan, onBlocked });

  if (detailQ.isLoading) {
    return (
      <div className="flex flex-1 items-center justify-center text-slate-400">
        <Loader2 className="size-7 animate-spin" />
      </div>
    );
  }
  if (detailQ.isError || !detail) {
    return (
      <div className="m-3 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
        Не удалось загрузить заказ: {extractErrorMessage(detailQ.error, "ошибка")}
      </div>
    );
  }

  const gate = computeGate(detail);
  const empty = emptyPallets(pallets);
  const completed = detail.header.status === "completed";
  // Перемещение: сверку раскладки не запрашиваем, состояние — по плану сессии.
  const planCheck: PlanCheck =
    kind === "customer" ? planCheckState(planReviewQ, detail) : transferPlanCheckState(detail);

  if (completed) {
    return (
      <CompletedScreen
        detail={detail}
        photoCount={(photosQ.data ?? []).length}
        onReopen={() => setDialog("reopen")}
        onClear={() => setDialog("clear")}
        onOpenPositions={() => setSheet({ kind: "positions", filter: "all" })}
        onBack={() => navigate("/order-check")}
        dialog={dialog}
        onCloseDialog={() => setDialog(null)}
        onConfirmReopen={() => reopenM.mutate(false)}
        onConfirmClear={() => reopenM.mutate(true)}
        reopenBusy={reopenM.isPending}
        sheet={sheet}
        onCloseSheet={() => setSheet(null)}
      />
    );
  }

  return (
    <>
      <input ref={scan.scanInputRef} {...scan.scanInputProps} className="pointer-events-none fixed left-0 top-0 size-px opacity-0" />

      {/* Z1 аппбар */}
      <AppBar
        detail={detail}
        chip={busy ? "busy" : scanIndexQ.isError ? "warn" : scanEnabled ? "ready" : "paused"}
        onBack={() => navigate("/order-check")}
        planCheck={planCheck}
        onOpenPlanCheck={() => setSheet({ kind: "plan-check" })}
        menu={{
          showCamera: scanMethod === "camera" || scanMethod === "both",
          onCamera: () => setCameraScan(true),
          onManual: () => setManualOpen(true),
          onReloadIndex: () => {
            void scanIndexQ.refetch();
            toast.success("Перезагружаю индекс ШК…");
          },
          onClear: () => setDialog("clear"),
        }}
      />

      {/* Z2 прогресс */}
      <Progress
        detail={detail}
        pallet={activeP}
        gate={gate}
        onTapOrder={() => setSheet({ kind: "positions", filter: filterForGate(gate.state) })}
        onTapPallet={() => setSheet({ kind: "pallet" })}
      />

      {/* Z3 чипы паллет */}
      <PalletChips
        pallets={pallets}
        active={activePallet}
        photos={photosByPallet}
        /* В директиве число паллет задаёт бот: своя была бы мертворождённой —
           плана у неё нет, значит ни один скан на неё не ляжет. */
        canAdd={!detail.is_directive && (!activeP?.writable || activeHasItems)}
        adding={createPalletM.isPending}
        /* Нередактируемую активной не делаем — тап сразу открывает её просмотр. */
        onSelect={(p) =>
          p.id === activePallet || !p.writable
            ? navigate(`/order-check/orders/${detail.header.order_ref}/pallets/${p.id}${kindSuffix(kind)}`)
            : setActivePallet(p.id)
        }
        onAdd={requestNewPallet}
      />

      <DeletedCharacteristicBanner lines={deletedCharacteristicLines(detail.needs)} />

      {/* ⚠️ Списка позиций паллеты на основном экране БОЛЬШЕ НЕТ. Он занимал до
          38 % высоты, а строки всё равно не влезали и резались многоточием —
          то есть место тратилось, а прочитать наименование было нельзя.
          Теперь он в шторке (SH-PAL), которая открывается тапом по числам
          паллеты в прогрессе: там строки переносятся целиком. */}

      {/* Z4 герой */}
      <div className="min-h-0 flex-1 overflow-y-auto p-3">
        {blocked && (
          <div className="mb-2">
            <BlockedScanBanner blocked={blocked} onClose={() => setBlocked(null)} />
          </div>
        )}
        <Hero
          hero={hero}
          gateReady={gate.state === "ready"}
          activeLabel={activeP?.label ?? "Паллета 1"}
          remaining={detail.qty_needed_total - detail.qty_scanned_total}
          onUndo={(id) => undoM.mutate(id)}
          onSetQty={(itemId, count, coef) => setQtyM.mutateAsync({ itemId, count, coef })}
          onFirstPallet={() => createPalletM.mutate()}
          onAddExtra={(resolved, s) => setDraft(makeExtraDraft(resolved, s, activePallet))}
          onDismiss={() => setHero({ kind: "idle" })}
        />
      </div>

      {/* Z5 сводка активной паллеты */}
      {activeP && (
        <button
          type="button"
          onClick={() => navigate(`/order-check/orders/${detail.header.order_ref}/pallets/${activeP.id}${kindSuffix(kind)}`)}
          className="flex items-center justify-between border-t bg-white px-3 py-2 text-left"
        >
          <span className="text-[13px] font-bold">{activeP.label}</span>
          <span className="flex items-center gap-2 text-[11px] text-slate-500">
            {activeP.items_count} поз · {fmtQty(activeP.qty)} шт{activeP.boxes > 0 ? ` · ${fmtQty(activeP.boxes)} кор` : ""}
            {activeP.items_count === 0 && <EmptyPalletBadge />}
            {/* Пустой паллете фото не нужно (`palletsMissingPhoto` её не просит) — «фото нет» лгало бы. */}
            {activeP.items_count > 0 && <PhotoBadge n={photosByPallet[activeP.id] ?? 0} />}
            <ChevronRight className="size-4 text-slate-300" />
          </span>
        </button>
      )}

      {/* Z6 гейт */}
      <Gate
        gate={gate}
        finalizing={finalizeM.isPending}
        onTap={() => {
          if (gate.state === "ready") setDialog("fin");
          else if (gate.state === "under" || gate.state === "over")
            setSheet({ kind: "positions", filter: filterForGate(gate.state) });
        }}
      />

      {/* Кнопка запуска камеры-сканера (при способе = камера/оба) — на ТСД нет */}
      {(scanMethod === "camera" || scanMethod === "both") && !cameraScan && (
        <button
          type="button"
          onClick={() => setCameraScan(true)}
          className="fixed bottom-24 right-4 z-30 flex h-14 items-center gap-2 rounded-full bg-sky-700 px-5 text-[14px] font-bold text-white shadow-lg"
        >
          <Camera className="size-5" /> Сканировать камерой
        </button>
      )}

      {cameraScan && (
        <Suspense fallback={null}>
          <div className="fixed inset-0 z-40 bg-black">
            <CameraScanner
              onDetect={(raw) => {
                setCameraScan(false);
                handleScan(raw);
              }}
              onClose={() => setCameraScan(false)}
            />
          </div>
        </Suspense>
      )}

      {/* SH1 уточнение */}
      {draft && (
        <ClarifySheet
          sessionId={sessionId}
          draft={draft}
          setDraft={setDraft}
          needs={detail.needs}
          onCancel={() => setDraft(null)}
          onConfirm={(loose, qty) => addResolved(qty != null ? { ...draft, qty } : draft, { loose })}
        />
      )}

      {/* SH-PLAN: раскладка 1С ↔ заказ 1С и план в портале (тап по значку в шапке) */}
      {sheet?.kind === "plan-check" && (
        <PlanCheckSheet
          state={planCheck}
          planCoverage={detail.plan_coverage}
          canReload={detail.header.status === "open"}
          reloading={reloadPlanM.isPending}
          onReload={() => reloadPlanM.mutate()}
          onRecheck={() => void planReviewQ.refetch()}
          planHref={canOpenPlan && kind === "customer" ? `/order-plan/orders/${detail.header.order_ref}` : null}
          onClose={() => setSheet(null)}
        />
      )}

      {/* SH-PAL: содержимое текущей паллеты (тап по её числам в прогрессе) */}
      {sheet?.kind === "pallet" && activeP && (
        <PalletSheet
          pallet={activeP}
          conflicts={detail.plan_conflicts}
          reloading={reloadPlanM.isPending}
          onReload={() => reloadPlanM.mutate()}
          onClose={() => setSheet(null)}
        />
      )}

      {/* SH2 позиции */}
      {sheet?.kind === "positions" && (
        <PositionsSheet
          detail={detail}
          filter={sheet.filter}
          onClose={() => setSheet(null)}
          onDeleteItem={(id) => removeItemM.mutate(id)}
          onDeleteItems={(ids) => removeItemsM.mutate(ids)}
          deletingItems={removeItemsM.isPending}
        />
      )}

      {/* OV-CAM фото (живая камера, без галереи) */}
      {photoModal && (
        <PhotoCapture
          title={`Сфотографируйте ${photoModal.label}${
            photoModal.then === "finalize" && photoQueue.length ? ` (осталось ещё ${photoQueue.length})` : ""
          }`}
          busy={stagePhotoM.isPending || finalizeM.isPending}
          progress={photoProgress.progress}
          onCapture={onPhotoCaptured}
          onSkip={
            photoRequired
              ? undefined
              : () => {
                  const then = photoModal.then;
                  setPhotoModal(null);
                  if (then === "new") createPalletM.mutate();
                  else finalizeM.mutate();
                }
          }
          onClose={() => setPhotoModal(null)}
        />
      )}

      {/* DLG-FIN */}
      {dialog === "fin" && (
        <Dialog
          title="Завершить проверку?"
          body={
            <>
              {`Будет создано: ${palletsWord(writablePallets.filter((p) => p.items_count > 0).length)} + расходная накладная (без проведения). Палетный лист и фото паллет уйдут в заказ 1С.`
                + (palletsMissingPhoto().length ? ` Сначала сфотографируем ${palletsWord(palletsMissingPhoto().length)} без снимка.` : "")}
              {/* Пустая паллета при завершении выпадает молча (НФНФ-000711) —
                  предупреждаем, но не запрещаем (решение владельца 23.10.2026). */}
              {empty.length > 0 && <EmptyPalletsWarning pallets={empty} />}
            </>
          }
          confirmLabel={
            palletsMissingPhoto().length
              ? `Фото и завершить${empty.length ? " без пустых" : ""}`
              : empty.length ? "Завершить без пустых" : "Подтвердить"
          }
          confirmBusy={finalizeM.isPending}
          onConfirm={requestFinalize}
          onCancel={() => setDialog(null)}
        />
      )}

      {/* DLG очистка открытого заказа */}
      {dialog === "clear" && (
        <Dialog
          title="Очистить заказ полностью?"
          body="Все паллеты и отсканированные позиции будут удалены. Действие необратимо."
          confirmLabel="Очистить"
          danger
          confirmBusy={clearM.isPending}
          onConfirm={() => clearM.mutate()}
          onCancel={() => setDialog(null)}
        />
      )}

      {/* SH4 ручной ввод кода */}
      {manualOpen && (
        <ManualCodeSheet
          onSubmit={(code) => {
            setManualOpen(false);
            void handleScan(code, true);
          }}
          onClose={() => setManualOpen(false)}
        />
      )}
    </>
  );
}

function ManualCodeSheet({ onSubmit, onClose }: { onSubmit: (code: string) => void; onClose: () => void }): React.ReactElement {
  const [v, setV] = useState("");
  return (
    <BottomSheet onClose={onClose} panelClassName="p-3">
      <div className="mb-2 text-[15px] font-bold">Ввести код вручную</div>
      <div className="flex gap-2">
        <input autoFocus value={v} onChange={(e) => setV(e.target.value)} placeholder="ШК / GTIN / GS1" className="h-[50px] flex-1 rounded-lg border px-3 font-mono text-[14px]" />
        <Button className="h-[50px] bg-sky-700" disabled={!v.trim()} onClick={() => onSubmit(v.trim())}>
          ОК
        </Button>
      </div>
    </BottomSheet>
  );
}

// ─────────────────────────── helpers ──────────────────────────────────────
// findNeed / requiredBoxCoef / norm — общие для order-check и transfer-check,
// живут в @/lib/order-check (единый источник гранулярности сверки).
/** Черновик скана из результата резолва (общий конструктор). */
function baseDraft(resolved: ResolvedScan, scan: CheckScan, palletId: string, mode: Draft["mode"]): Draft {
  return {
    palletId,
    scan,
    resolved,
    charRef: resolved.characteristic_ref,
    charName: resolved.characteristic_name,
    withoutChar: false,
    partiaRef: resolved.partia_ref,
    partiaName: resolved.partia_name,
    partiaSource: resolved.partia_source,
    withoutPartia: false,
    qty: resolved.qty_base || 1,
    mode,
  };
}
function makeExtraDraft(resolved: ResolvedScan, scan: CheckScan, palletId: string): Draft {
  return baseDraft(resolved, scan, palletId, "extra");
}

/** Панель плана активной паллеты (директивный режим).
 *
 * Показывает СТРОКИ ПЛАНА в порядке укладки, а не «нужно по заказу»: в этом
 * весь режим — кладовщик собирает конкретную паллету по конкретному заданию.
 * Закрытые строки уходят вниз и гаснут, следующая по плану — сверху.
 */
/** Карточка позиции — ОДНА на обе шторки экрана.
 *
 * ⚠️ Заведена потому, что шторки разъехались: заказ рисовал карточки с
 * артикулом, характеристикой и партией, а паллета — плоские строки списка, в
 * которых артикула не было вовсе. Один и тот же товар выглядел в них как два
 * разных, и сверять приходилось наименования, а не коды. Общая карточка
 * делает расхождение НЕВОЗМОЖНЫМ, а не «поправленным один раз».
 */
export function GoodsCard({
  status,
  article,
  name,
  characteristic,
  characteristicRef,
  partia,
  boxCoef = 1,
  note,
  scanned,
  needed,
  unit,
}: {
  status: NeedStatus;
  article?: string | null;
  name?: string | null;
  characteristic?: string | null;
  characteristicRef?: string | null;
  partia?: string | null;
  boxCoef?: number;
  note?: string;
  scanned: number;
  /** `null` — цели нет. У паллеты в свободном режиме знаменателя не
   *  существует: план ей никто не задавал, и придумывать его нельзя. */
  needed: number | null;
  unit: string;
}): React.ReactElement {
  return (
    <div className={cn("rounded-lg border p-2", STATUS_ROW[status])}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-1">
            {article ? <span className="font-mono text-[14px] font-bold">{article}</span> : null}
            <CharacteristicBadge name={characteristic} characteristicRef={characteristicRef} />
            {partia ? (
              <span className="rounded border bg-white px-1 font-mono text-[10px]">парт. {partia}</span>
            ) : null}
            {boxCoef > 1 ? (
              <span className="rounded border border-sky-200 bg-sky-100 px-1 text-[10px] font-bold text-sky-800">
                короб ×{fmtQty(boxCoef)}
              </span>
            ) : null}
          </div>
          {/* ⚠️ Без `truncate`: наименование переносится целиком — ровно то,
              чего не хватало на основном экране. */}
          <div className="mt-0.5 text-[11px] leading-snug text-slate-600">{name}</div>
          {note ? (
            <div className={cn("text-[11px] font-semibold", STATUS_TEXT[status])}>{note}</div>
          ) : null}
        </div>
        <div className={cn("shrink-0 font-mono text-[17px] font-bold tabular-nums", STATUS_TEXT[status])}>
          {fmtQty(scanned)}
          <span className="text-[10px] text-slate-400">
            {needed === null ? ` ${unit}` : `/${fmtQty(needed)} ${unit}`}
          </span>
        </div>
      </div>
    </div>
  );
}

/** SH-PAL: что лежит и что осталось на ТЕКУЩЕЙ паллете.
 *
 * ⚠️ Это бывшая панель плана с основного экрана. Она занимала до 38 % высоты и
 * всё равно резала наименования многоточием — то есть место тратила, а
 * прочитать строку не давала. В шторке высоты хватает, и наименование
 * переносится целиком.
 *
 * Директивный режим показывает ПЛАН (что бот велел положить и сколько уже
 * лежит), свободный — фактически собранное: плана там нет и придумывать его
 * нельзя, целевого количества у паллеты в этом режиме не существует.
 */
function PalletSheet({
  pallet,
  conflicts,
  reloading,
  onReload,
  onClose,
}: {
  pallet: PalletSummary;
  conflicts: PlanConflict[];
  reloading: boolean;
  onReload: () => void;
  onClose: () => void;
}): React.ReactElement {
  const plan = pallet.plan ?? [];
  // Статус строки и порог сравнения — общие для всего экрана (`needStatus`):
  // своя копия правила «сколько считать собранным» разъехалась бы с гейтом,
  // и панель показывала бы «собрана», когда завершить нельзя.
  const rows = [...plan]
    .map((l) => ({ line: l, status: needStatus({ qty_needed: l.qty_plan, qty_scanned: l.qty_scanned }) }))
    .sort((a, b) => {
      const done = (s: string) => (s === "done" ? 1 : 0);
      return done(a.status) - done(b.status) || a.line.line_number - b.line.line_number;
    });
  const planTotal = plan.reduce((s, l) => s + l.qty_plan, 0);
  const planScanned = plan.reduce((s, l) => s + l.qty_scanned, 0);
  const left = plan.reduce((s, l) => s + Math.max(0, l.qty_plan - l.qty_scanned), 0);
  const mine = conflicts.filter((c) => c.pallet_label === pallet.label);

  // Свободный режим: сворачиваем СКАНЫ в товары. `items` — это лента сканов, и
  // один товар, взятый десятью коробками, дал бы десять одинаковых строк:
  // спрашивают-то «что на паллете», а не «сколько раз пикнули».
  const goods = useMemo(() => {
    const m = new Map<
      string,
      {
        key: string;
        article: string;
        name: string;
        characteristic: string;
        characteristicRef: string;
        partia: string;
        qty: number;
        boxes: number;
      }
    >();
    for (const it of pallet.items) {
      const key = `${it.nomenclature_ref ?? ""}|${it.characteristic_ref ?? ""}|${it.partia_ref ?? ""}`;
      const row = m.get(key) ?? {
        key,
        article: it.nomenclature_article ?? "",
        name: it.nomenclature_name || "—",
        characteristic: it.characteristic_name ?? "",
        characteristicRef: it.characteristic_ref ?? "",
        partia: it.partia_name ?? "",
        qty: 0,
        boxes: 0,
      };
      row.qty += it.qty_base;
      row.boxes += it.boxes;
      m.set(key, row);
    }
    return [...m.values()].sort((a, b) => (a.article || a.name).localeCompare(b.article || b.name));
  }, [pallet.items]);

  const total = plan.length > 0 ? planTotal : goods.reduce((s, g) => s + g.qty, 0);
  const done = plan.length > 0 ? planScanned : total;

  return (
    <BottomSheet onClose={onClose} panelClassName="flex max-h-[90%] flex-col">
      <div className="flex items-center justify-between border-b p-3">
        <div className="min-w-0">
          <div className="text-[15px] font-bold">{pallet.label ?? "Паллета"}</div>
          {palletNumber(pallet) ? (
            <div className="font-mono text-[11px] text-slate-500">{palletNumber(pallet)}</div>
          ) : null}
        </div>
        <div className="flex items-center gap-2">
          <span className="text-[12px] text-slate-500">
            {fmtQty(done)} / {fmtQty(total)} шт
          </span>
          <button type="button" onClick={onClose} className="flex size-8 items-center justify-center">
            <X className="size-4" />
          </button>
        </div>
      </div>

      {plan.length > 0 && (
        <div className="flex items-center justify-between gap-2 border-b px-3 py-2">
          <span
            className={cn(
              "text-[12px] font-semibold",
              left > REMAINING_EPS ? "text-amber-700" : "text-emerald-700",
            )}
          >
            {left > REMAINING_EPS ? `осталось ${fmtQty(left)} шт` : "паллета собрана"}
          </span>
          <button
            type="button"
            onClick={onReload}
            disabled={reloading}
            className="text-[12px] font-semibold text-sky-700 underline"
          >
            {reloading ? "обновляю…" : "обновить план"}
          </button>
        </div>
      )}

      {pallet.box_plan && <BoxPlanProgress plan={pallet.box_plan} />}

      {mine.length > 0 && (
        <div className="border-b bg-amber-50 px-3 py-2 text-[12px] text-amber-900">
          Собрано больше, чем в новом плане:{" "}
          {mine.map((c) => `${c.nomenclature_name} ${fmtQty(c.qty_scanned)}/${fmtQty(c.qty_plan)}`).join("; ")}.
          Снимите лишнее с паллеты.
        </div>
      )}

      <div className="flex-1 space-y-1.5 overflow-y-auto p-2">
        {plan.length > 0
          ? rows.map(({ line: l, status }) => (
              <GoodsCard
                key={l.line_number}
                status={status}
                article={l.nomenclature_article}
                name={l.nomenclature_name || l.nomenclature_ref.slice(0, 8)}
                characteristic={l.characteristic_name}
                characteristicRef={l.characteristic_ref}
                partia={l.partia_name}
                note={
                  status === "done"
                    ? "✓ собрано точно"
                    : status === "over"
                      ? `+${fmtQty(l.qty_scanned - l.qty_plan)} шт лишних`
                      : `осталось ${fmtQty(Math.max(0, l.qty_plan - l.qty_scanned))} шт`
                }
                scanned={l.qty_scanned}
                needed={l.qty_plan}
                unit="шт"
              />
            ))
          : goods.map((g) => (
              <GoodsCard
                key={g.key}
                status="idle"
                article={g.article}
                name={g.name}
                characteristic={g.characteristic}
                characteristicRef={g.characteristicRef}
                partia={g.partia}
                note={g.boxes > 0 ? `${fmtQty(g.boxes)} ${plural(g.boxes, ["короб", "короба", "коробов"])}` : ""}
                scanned={g.qty}
                needed={null}
                unit="шт"
              />
            ))}
        {plan.length === 0 && goods.length === 0 && (
          <p className="py-6 text-center text-[13px] text-slate-400">На паллете пока ничего нет.</p>
        )}
      </div>
    </BottomSheet>
  );
}

interface PlanErrorDetail {
  code: string;
  detail: string;
  pallet?: string;
}

/** Заголовки отказов директивного режима. Текст самой причины приходит с
 *  бэкенда — он знает и номер паллеты, и ожидаемую партию. */
const PLAN_ERROR_TITLE: Record<string, string> = {
  other_pallet: "Это на другую паллету",
  over_plan: "По плану уже собрано",
  wrong_partia: "Другая партия",
  not_in_plan: "Нет в плане заказа",
  pallet_without_plan: "У паллеты нет плана",
};

type GateState = "empty" | "under" | "over" | "ready";
interface GateInfo {
  state: GateState;
  remaining: number;
  remainPositions: number;
  over: number;
  extras: number;
}
function computeGate(detail: SessionDetail): GateInfo {
  const hasItems = detail.pallets.some((p) => p.items_count > 0);
  const remainLines = detail.needs.filter((n) => n.qty_remaining > REMAINING_EPS);
  const remaining = remainLines.reduce((s, n) => s + n.qty_remaining, 0);
  const over = detail.needs.reduce((s, n) => s + Math.max(0, n.qty_scanned - n.qty_needed), 0);
  const extras = detail.extras.length;
  let state: GateState = "ready";
  if (!hasItems) state = "empty";
  else if (over > REMAINING_EPS || extras > 0) state = "over";
  else if (remaining > REMAINING_EPS) state = "under";
  return { state, remaining, remainPositions: remainLines.length, over, extras };
}

/** Какой фильтр SH2 открыть по состоянию гейта. */
function filterForGate(state: GateState): PosFilter {
  return state === "under" ? "remain" : state === "over" ? "problem" : "all";
}

// ─────────────────────────── zones ────────────────────────────────────────
function AppBar({
  detail,
  chip,
  onBack,
  planCheck,
  onOpenPlanCheck,
  menu,
}: {
  detail: SessionDetail;
  chip: "ready" | "busy" | "paused" | "warn";
  onBack: () => void;
  /** Значок сверки раскладки; нет — экран завершённой проверки. */
  planCheck?: PlanCheck;
  onOpenPlanCheck?: () => void;
  menu?: { showCamera: boolean; onCamera: () => void; onManual: () => void; onReloadIndex: () => void; onClear: () => void };
}): React.ReactElement {
  const [info, setInfo] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const h = detail.header;
  const chipMap = {
    ready: { dot: "bg-emerald-400", bg: "bg-emerald-500/20", t: "Сканер готов" },
    busy: { dot: "bg-sky-400", bg: "bg-sky-500/20", t: "Ищем в 1С…" },
    paused: { dot: "bg-slate-400", bg: "bg-white/10", t: "Скан на паузе" },
    warn: { dot: "bg-amber-400", bg: "bg-amber-500/20", t: "Индекс ШК не загружен" },
  }[chip];
  const item = "block w-full px-3 py-2.5 text-left text-[13px] hover:bg-slate-100";
  return (
    <div className="relative bg-slate-900 text-white">
      <div className="flex h-[46px] items-center gap-1 px-1">
        <button type="button" onClick={onBack} className="flex size-9 items-center justify-center" aria-label="Назад">
          <ArrowLeft className="size-5" />
        </button>
        <div className="min-w-0 flex-1 leading-tight">
          <button type="button" onClick={() => setInfo((s) => !s)} className="flex w-full min-w-0 items-center gap-1.5 text-left">
            <span className="truncate font-mono text-[13px] font-bold">{h.order_number}</span>
            {/* Комментарий менеджера виден ЗНАЧКОМ: без него кладовщик не
                знает, что по заказу вообще есть условия, и не станет никуда
                тапать. Раскрывается тем же тапом, что и шапка. */}
            {h.comment && (
              <span className="flex items-center gap-0.5 rounded bg-amber-400 px-1 text-[9px] font-bold text-amber-950">
                <MessageSquare className="size-2.5" />
                примечание
              </span>
            )}
          </button>
          {/* Значок сверки раскладки — ВТОРОЙ строкой, перед контрагентом: в
              первой его негде поставить на ТСД (360 px), не обрезав номер
              заказа, а контрагент и так обрезается многоточием. */}
          <div className="flex min-w-0 items-center gap-1">
            {planCheck && onOpenPlanCheck ? <PlanCheckChip state={planCheck} onOpen={onOpenPlanCheck} /> : null}
            <button
              type="button"
              onClick={() => setInfo((s) => !s)}
              className="min-w-0 flex-1 truncate text-left text-[10px] text-slate-400"
            >
              → {h.kontragent_name}
            </button>
          </div>
        </div>
        <button
          type="button"
          onClick={() => menu && setMenuOpen((s) => !s)}
          className={cn("mr-1 flex h-[30px] items-center gap-1.5 rounded-full px-2.5", chipMap.bg)}
        >
          <span className={cn("size-2 rounded-full", chipMap.dot)} />
          <span className="text-[11px] font-semibold">{chipMap.t}</span>
        </button>
      </div>
      {info && (
        <div className="border-t border-white/10 px-3 py-1.5 text-[11px] text-slate-300">
          {h.organization_name} · {fmtDate(h.order_date)}
          {h.comment && (
            <div className="mt-1 whitespace-pre-wrap rounded bg-amber-400/15 px-2 py-1 text-amber-200">
              {h.comment}
            </div>
          )}
        </div>
      )}
      {menuOpen && menu && (
        <>
          <div className="fixed inset-0 z-30" onClick={() => setMenuOpen(false)} />
          <div className="absolute right-2 top-[44px] z-40 w-56 overflow-hidden rounded-lg bg-white text-slate-800 shadow-xl">
            {menu.showCamera && (
              <button type="button" className={item} onClick={() => { setMenuOpen(false); menu.onCamera(); }}>Камера-сканер</button>
            )}
            <button type="button" className={item} onClick={() => { setMenuOpen(false); menu.onManual(); }}>Ввести код вручную</button>
            <button type="button" className={item} onClick={() => { setMenuOpen(false); menu.onReloadIndex(); }}>Повторить загрузку индекса ШК</button>
            <button type="button" className={cn(item, "text-red-600")} onClick={() => { setMenuOpen(false); menu.onClear(); }}>Очистить заказ полностью</button>
          </div>
        </>
      )}
    </div>
  );
}

/** Прогресс: слева — весь заказ, справа — ТЕКУЩАЯ паллета.
 *
 * Две величины, а не одна, потому что вопросов у кладовщика тоже два: «сколько
 * осталось по заказу» и «эту паллету можно закрывать?». Пока на экране был
 * только заказ, второй ответ приходилось собирать из плана глазами.
 *
 * ⚠️ У паллеты знаменатель есть ТОЛЬКО в директиве: план ей задал бот. В
 * свободном режиме (опт) паллету набирают на глаз, целевого количества у неё
 * не существует — и придумывать его нельзя, поэтому там показываем просто
 * набранное.
 */
/** Прогресс: слева заказ целиком, справа текущая паллета.
 *
 * ⚠️ Половины — РАЗНЫЕ кнопки, и это не дробление ради дробления: у кладовщика
 * два вопроса («сколько осталось по заказу» и «что ещё на эту паллету»), и
 * ответы у них разные. Пока вся полоса была одной кнопкой, тап по числам
 * паллеты открывал сводку по ЗАКАЗУ — то есть отвечал не на тот вопрос,
 * который задали. */
function Progress({
  detail,
  pallet,
  gate,
  onTapOrder,
  onTapPallet,
}: {
  detail: SessionDetail;
  pallet: PalletSummary | undefined;
  gate: GateInfo;
  onTapOrder: () => void;
  onTapPallet: () => void;
}): React.ReactElement {
  const sc = detail.qty_scanned_total;
  const nd = detail.qty_needed_total;
  const pct = nd > 0 ? Math.min(100, (sc / nd) * 100) : 0;
  const over = gate.over > REMAINING_EPS || gate.extras > 0;
  const exact = gate.state === "ready";
  const bar = over ? "bg-red-500" : exact ? "bg-emerald-500" : "bg-amber-500";
  const plan = pallet?.plan ?? [];
  const palletPlan = plan.reduce((acc, l) => acc + l.qty_plan, 0);
  const palletLeft = plan.reduce((acc, l) => acc + Math.max(0, l.qty_plan - l.qty_scanned), 0);
  const palletDone = plan.length > 0 && palletLeft <= REMAINING_EPS;
  // Перебор на паллете красим так же, как перебор по заказу: «собрана» и «на
  // ней лишнее» — разные состояния, а зелёное число про второе молчало бы.
  const palletOver = plan.length > 0 && (pallet?.qty ?? 0) > palletPlan + REMAINING_EPS;
  return (
    <div className="w-full border-b bg-white px-3.5 py-2">
      <div className="flex items-end justify-between gap-3">
        {/* Левая половина — заказ целиком. */}
        <button type="button" onClick={onTapOrder} className="min-w-0 text-left">
          <span className="block text-[11px] font-semibold text-slate-500">Заказ</span>
          <span className="flex items-baseline gap-1">
            <span className={cn("font-mono text-[17px] font-bold tabular-nums", over && "text-red-600")}>{fmtQty(sc)}</span>
            <span className="text-[13px] text-slate-500">/ {fmtQty(nd)} шт</span>
            <ChevronRight className="size-4 shrink-0 text-slate-300" />
          </span>
        </button>
        {/* Правая — ТОЛЬКО текущая паллета. */}
        {pallet && (
          <button type="button" onClick={onTapPallet} className="text-right">
            <span className="block text-[11px] font-semibold text-slate-500">{pallet.label ?? "Паллета"}</span>
            <span className="flex items-baseline justify-end gap-1">
              <span
                className={cn(
                  "font-mono text-[17px] font-bold tabular-nums",
                  palletOver ? "text-red-600" : palletDone && "text-emerald-600",
                )}
              >
                {fmtQty(pallet.qty)}
              </span>
              <span className="text-[13px] text-slate-500">
                {plan.length > 0 ? `/ ${fmtQty(palletPlan)} шт` : "шт"}
              </span>
              <ChevronRight className="size-4 shrink-0 text-slate-300" />
            </span>
          </button>
        )}
      </div>
      <div className="mt-1.5 h-2 w-full overflow-hidden rounded bg-slate-200">
        <div className={cn("h-full rounded transition-[width]", bar)} style={{ width: `${pct}%` }} />
      </div>
      <div className="mt-1 flex flex-wrap gap-1.5 text-[10px] font-bold">
        {gate.state === "under" && (
          <span className="rounded bg-amber-100 px-1.5 py-0.5 text-amber-700">
            Осталось {fmtQty(gate.remaining)} шт · {gate.remainPositions} {plural(gate.remainPositions, ["позиция", "позиции", "позиций"])}
          </span>
        )}
        {gate.over > REMAINING_EPS && <span className="rounded bg-red-100 px-1.5 py-0.5 text-red-700">Перебор +{fmtQty(gate.over)} шт</span>}
        {gate.extras > 0 && <span className="rounded bg-red-100 px-1.5 py-0.5 text-red-700">⚠ лишние: {gate.extras}</span>}
        {pallet?.box_plan && (
          <span
            className={cn(
              "rounded px-1.5 py-0.5",
              boxPlanDone(pallet.box_plan)
                ? "bg-emerald-100 text-emerald-700"
                : "bg-amber-100 text-amber-700",
            )}
          >
            Коробки по плану {pallet.box_plan.filled}/{pallet.box_plan.planned}
          </span>
        )}
      </div>
    </div>
  );
}

function PalletChips({
  pallets,
  active,
  photos,
  canAdd,
  adding,
  onSelect,
  onAdd,
}: {
  pallets: PalletSummary[];
  active: string;
  photos: Record<string, number>;
  canAdd: boolean;
  adding: boolean;
  onSelect: (p: PalletSummary) => void;
  onAdd: () => void;
}): React.ReactElement {
  return (
    <div className="flex items-center gap-1.5 overflow-x-auto border-b bg-slate-100 px-2 py-1.5">
      {pallets.map((p, i) => {
        const on = p.id === active;
        const hasPhoto = (photos[p.id] ?? 0) > 0;
        return (
          <button
            key={p.id}
            type="button"
            onClick={() => onSelect(p)}
            className={cn(
              "flex shrink-0 items-center gap-1.5 rounded-lg border px-2.5 py-1.5",
              on ? "border-sky-700 bg-sky-700 text-white" : "border-slate-300 bg-white",
              !p.writable && "opacity-60",
            )}
          >
            <span className="text-[12px] font-bold">Пал. {p.position ?? i + 1}</span>
            <span className={cn("text-[11px]", on ? "text-sky-100" : "text-slate-500")}>{fmtQty(p.qty)} шт</span>
            {/* Пустой — бейдж вместо отметки фото: фото у неё не просят. */}
            {p.items_count === 0 ? (
              <EmptyPalletBadge />
            ) : (
              <span className={cn("text-[11px]", hasPhoto ? (on ? "text-emerald-200" : "text-emerald-600") : on ? "text-amber-200" : "text-slate-400")}>
                {hasPhoto ? "◉" : "○"}
              </span>
            )}
          </button>
        );
      })}
      <button
        type="button"
        onClick={onAdd}
        disabled={adding || !canAdd}
        className="flex shrink-0 items-center gap-1 rounded-lg border border-dashed border-sky-400 px-2.5 py-1.5 text-[12px] text-sky-700 disabled:border-slate-300 disabled:text-slate-400"
      >
        {adding ? <Loader2 className="size-3 animate-spin" /> : <Plus className="size-3" />} Новая
      </button>
    </div>
  );
}

function PhotoBadge({ n }: { n: number }): React.ReactElement {
  return n > 0 ? (
    <span className="rounded bg-emerald-50 px-1.5 py-0.5 text-[10px] font-semibold text-emerald-700">◉ фото {n}</span>
  ) : (
    <span className="rounded bg-amber-50 px-1.5 py-0.5 text-[10px] font-semibold text-amber-700">○ фото нет</span>
  );
}

// ─────────────────────────── Hero ─────────────────────────────────────────
function Hero({
  hero,
  gateReady,
  activeLabel,
  remaining,
  onUndo,
  onSetQty,
  onFirstPallet,
  onAddExtra,
  onDismiss,
}: {
  hero: Hero;
  gateReady?: boolean;
  activeLabel: string;
  remaining: number;
  onUndo: (id: string) => void;
  onSetQty: (itemId: string, count: number, coef: number) => Promise<unknown>;
  onFirstPallet: () => void;
  onAddExtra: (r: ResolvedScan, s: CheckScan) => void;
  onDismiss: () => void;
}): React.ReactElement {
  if (hero.kind === "idle" && gateReady) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-2 rounded-xl border-2 border-emerald-200 bg-emerald-50 text-center">
        <CheckCircle2 className="size-9 text-emerald-600" />
        <div className="text-[18px] font-bold text-emerald-700">Всё сходится — можно завершать</div>
        <div className="text-[12px] text-emerald-700">лишних нет · спускайтесь к кнопке ниже ↓</div>
      </div>
    );
  }
  if (hero.kind === "nopallets") {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-3 rounded-xl border-2 border-dashed border-slate-300 p-4 text-center">
        <div className="text-[16px] font-bold text-slate-700">Создайте паллету, чтобы начать</div>
        <div className="text-[12px] text-slate-500">Первый скан создаст «Паллета 1» автоматически</div>
        <Button className="h-[54px] w-full bg-sky-700 hover:bg-sky-800" onClick={onFirstPallet}>
          <Plus className="size-5" /> Паллета 1
        </Button>
      </div>
    );
  }
  if (hero.kind === "idle") {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-2 text-center">
        <div className="flex size-14 items-center justify-center rounded-2xl bg-slate-100">
          <ScanLine className="size-7 text-slate-400" />
        </div>
        <div className="text-[16px] font-bold">Сканируйте товар</div>
        <div className="text-[12px] text-slate-500">активная: {activeLabel}</div>
        {remaining > REMAINING_EPS && <div className="text-[11px] text-slate-400">осталось {fmtQty(remaining)} шт по заказу</div>}
      </div>
    );
  }
  if (hero.kind === "busy") {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-2 rounded-xl border border-sky-200 bg-sky-50 text-center">
        <Loader2 className="size-8 animate-spin text-sky-700" />
        <div className="text-[16px] font-bold text-sky-900">Ищем в 1С…</div>
        <div className="font-mono text-[11px] text-slate-500">{hero.raw.slice(0, 40)}</div>
      </div>
    );
  }
  if (hero.kind === "error") {
    return (
      <div className="rounded-xl border-2 border-red-200 bg-red-50 p-3">
        <div className="text-[18px] font-bold text-red-700">{hero.title}</div>
        {/* pre-line: отказ по коробке — строка на позицию. */}
        <div className="mt-1 whitespace-pre-line text-[12px] text-red-900">{hero.detail}</div>
        <div className="mt-2 break-all rounded bg-white p-2 font-mono text-[11px] text-slate-500">{hero.raw}</div>
        <div className="mt-2 flex gap-2">
          {hero.retry && (
            <Button className="h-12 flex-1 bg-red-700 hover:bg-red-800" onClick={hero.retry}>
              <RotateCcw className="size-4" /> Повторить
            </Button>
          )}
          <Button variant="outline" className="h-12 flex-1" onClick={onDismiss}>
            Продолжить скан
          </Button>
        </div>
      </div>
    );
  }
  if (hero.kind === "extra") {
    const r = hero.resolved;
    return (
      <div className="rounded-xl border-2 border-red-200 bg-red-50 p-3">
        <div className="text-[19px] font-bold text-red-700">⛔ Нет в заказе</div>
        <div className="mt-1 font-mono text-[19px]">{r.nomenclature_article}</div>
        <div className="text-[12px] text-slate-600">{r.nomenclature_name}</div>
        <div className="mt-1 text-[12px] font-bold text-red-700">Скан не сохранён</div>
        <div className="mt-2 flex justify-end">
          <Button variant="outline" className="h-10 border-red-300 text-red-700" onClick={() => onAddExtra(hero.resolved, hero.scan)}>
            Добавить всё равно
          </Button>
        </div>
      </div>
    );
  }
  if (hero.kind === "box") return <BoxAcceptedCard box={hero.box} />;
  // ok — карточка авто-учёта со степпером количества (N одинаковых коробов/штук).
  return <OkCard key={hero.item.itemId} item={hero.item} onUndo={onUndo} onSetQty={onSetQty} />;
}

/** Карточка авто-учёта: можно тут же задать КОЛИЧЕСТВО одинаковых коробов/штук
 * (чтобы не сканировать каждую коробку). Меняет qty позиции на бэке → рефетч. */
export function OkCard({
  item: it,
  onUndo,
  onSetQty,
}: {
  item: ItemView2;
  onUndo: (id: string) => void;
  onSetQty: (itemId: string, count: number, coef: number) => Promise<unknown>;
}): React.ReactElement {
  // Количество единиц скана (коробов при коэф>1, штук при коэф=1) в этой позиции.
  const initCount = Math.max(1, Math.round(it.added / (it.coef || 1)));
  const [count, setCount] = useState(initCount);
  // Сколько записано на сервере: отказ правки откатывает поле к нему, иначе
  // карточка показывала бы количество, которого в базе нет.
  // Ref, а не состояние: ответы на быстрые «+ +» приходят позже следующего тапа.
  const saved = useRef(initCount);
  const addedBase = count * it.coef;
  const boxUnit = it.coef > 1;
  // it.need — снимок ДО добавления; «собрано/осталось» считаем с учётом count.
  const scannedNow = it.need ? it.need.qty_scanned + addedBase : 0;
  const st = it.need ? needStatus({ qty_needed: it.need.qty_needed, qty_scanned: scannedNow }) : "idle";
  const remain = it.need ? Math.max(0, it.need.qty_needed - scannedNow) : 0;
  const nCoef = it.need && it.need.unit_coef > 1 ? it.need.unit_coef : 1;
  const nUnit = nCoef > 1 ? "кор." : "шт";
  const showN = (base: number): string => fmtQty(nCoef > 1 ? base / nCoef : base);
  const commit = (v: number): void => {
    const n = Math.max(1, Math.round(v) || 1);
    setCount(n);
    if (n === saved.current) return;
    onSetQty(it.itemId, n, it.coef).then(
      () => {
        saved.current = n;
      },
      // Ошибку показывает мутация (тост); здесь — только откат поля.
      () => setCount(saved.current),
    );
  };
  return (
    <div className="oc-flash rounded-xl border-2 border-emerald-300 bg-emerald-50/60 p-3">
      <div className="flex items-center gap-2">
        <span className="font-mono text-[21px] font-bold">{it.article}</span>
        {boxUnit && <span className="rounded border border-sky-200 bg-sky-100 px-1.5 py-0.5 text-[10px] font-bold text-sky-800">короб ×{fmtQty(it.coef)}</span>}
        <Check className="ml-auto size-5 text-emerald-600" />
      </div>
      <div className="mt-2 grid grid-cols-2 gap-1.5">
        <Tile label="ХАРАКТ." value={it.char || "без характ."} />
        <Tile label="ПАРТИЯ" value={it.partia || "без партии"} mono={!!it.partia} />
        <Tile label="В КОРОБЕ" value={boxUnit ? `× ${fmtQty(it.coef)} шт` : "штука"} />
        <Tile label="ДОБАВЛЕНО" value={`+${fmtQty(addedBase)} шт`} accent />
      </div>
      <div className="mt-2 line-clamp-2 text-[11px] text-slate-600">{it.name}</div>

      {/* Прогресс по заказу — ВЫШЕ степпера, чтобы «нужно/собрано/осталось» было
          сразу на виду (не уезжало под ввод количества). */}
      {it.need && (
        <div className={cn("mt-2 rounded-md border p-2 text-[13px]", STATUS_ROW[st], STATUS_TEXT[st])}>
          <div className="flex items-center justify-between">
            <span>нужно {showN(it.need.qty_needed)} · собрано {showN(scannedNow)} {nUnit}</span>
            <span className="text-[14px] font-bold">
              {st === "done" ? "✓ закрыто" : st === "over" ? `ПЕРЕБОР` : `ОСТАЛОСЬ ${showN(remain)} ${nUnit}`}
            </span>
          </div>
        </div>
      )}

      {/* Степпер количества одинаковых коробов/штук. Коробке с SSCC его нет:
          это одно место реестра с известным составом, «ещё 3 таких» — не она. */}
      {!it.sscc && (
        <div className="mt-2 rounded-lg border bg-white p-2">
          <div className="mb-1 text-[11px] font-semibold text-slate-500">{boxUnit ? "Коробов" : "Штук"} в этой позиции:</div>
          <div className="flex items-center justify-center gap-3">
            <button type="button" className="size-11 rounded-lg border text-[22px] disabled:opacity-40" disabled={count <= 1} onClick={() => commit(count - 1)}>−</button>
            <input
              type="number"
              inputMode="numeric"
              value={count}
              onChange={(e) => setCount(Math.max(1, Math.round(Number(e.target.value) || 1)))}
              onBlur={() => commit(count)}
              onKeyDown={(e) => { if (e.key === "Enter") (e.target as HTMLInputElement).blur(); }}
              className="h-11 w-16 rounded-lg border text-center font-mono text-[22px] font-bold tabular-nums"
            />
            <button type="button" className="size-11 rounded-lg border text-[22px]" onClick={() => commit(count + 1)}>+</button>
          </div>
          {boxUnit && <div className="mt-1 text-center text-[11px] text-slate-500">= {fmtQty(addedBase)} шт</div>}
        </div>
      )}

      <Button variant="outline" className="mt-2 h-11 w-full" onClick={() => onUndo(it.itemId)}>
        <RotateCcw className="size-4" /> Отменить последний скан
      </Button>
    </div>
  );
}

function Tile({ label, value, mono, accent }: { label: string; value: string; mono?: boolean; accent?: boolean }): React.ReactElement {
  return (
    <div className={cn("rounded-lg border px-2 py-1.5", accent ? "border-emerald-300 bg-emerald-50" : "border-slate-200 bg-white")}>
      <div className="text-[9px] font-bold text-slate-400">{label}</div>
      <div className={cn("text-[15px] font-bold", mono && "font-mono", accent && "text-emerald-700")}>{value}</div>
    </div>
  );
}

// ─────────────────────────── Gate ─────────────────────────────────────────
function Gate({ gate, finalizing, onTap }: { gate: GateInfo; finalizing: boolean; onTap: () => void }): React.ReactElement {
  let cls = "bg-slate-100 border-slate-200 text-slate-500";
  let label = "Отсканируйте товары";
  let disabled = true;
  if (finalizing) {
    cls = "bg-emerald-600 text-white";
    label = "Создаём документы в 1С…";
  } else if (gate.state === "under") {
    cls = "bg-amber-50 border-amber-300 text-amber-700";
    label = `Осталось ${fmtQty(gate.remaining)} шт · ${gate.remainPositions} ${plural(gate.remainPositions, ["позиция", "позиции", "позиций"])} ›`;
    disabled = false;
  } else if (gate.state === "over") {
    cls = "bg-red-50 border-red-300 text-red-700";
    label = `Перебор +${fmtQty(gate.over)} шт${gate.extras ? ` · лишних ${gate.extras}` : ""} ›`;
    disabled = false;
  } else if (gate.state === "ready") {
    cls = "bg-emerald-600 text-white border-emerald-600";
    label = "✓ Завершить — создать расходную";
    disabled = false;
  }
  return (
    <div className="border-t bg-white p-2">
      <button
        type="button"
        disabled={disabled || finalizing}
        onClick={onTap}
        className={cn("flex h-14 w-full items-center justify-center gap-2 rounded-[10px] border text-[14px] font-bold", cls)}
      >
        {finalizing && <Loader2 className="size-4 animate-spin" />}
        {label}
      </button>
    </div>
  );
}

// ─────────────────────────── SH1 ──────────────────────────────────────────
function ClarifySheet({
  sessionId,
  draft,
  setDraft,
  needs,
  onCancel,
  onConfirm,
}: {
  sessionId: string;
  draft: Draft;
  setDraft: (d: Draft) => void;
  needs: NeedLine[];
  onCancel: () => void;
  onConfirm: (loose: boolean, qty?: number) => void;
}): React.ReactElement {
  const r = draft.resolved;
  const [partiaInput, setPartiaInput] = useState(draft.partiaName || draft.scan.batch || "");
  const partiaM = useMutation({
    mutationFn: (name: string) => lookupPartia(sessionId, r.nomenclature_ref, name),
    onSuccess: (p) => setDraft({ ...draft, partiaRef: p.ref, partiaName: p.name, partiaSource: "manual", withoutPartia: false }),
    onError: (e) => {
      const st = (e as { response?: { status?: number } })?.response?.status;
      toast.error(st === 404 ? "Партия не найдена в 1С" : extractErrorMessage(e, "Ошибка проверки партии"));
    },
  });
  const needChar = r.needs_characteristic && !draft.charRef && !draft.withoutChar;
  const partiaReady = draft.withoutPartia || !!draft.partiaRef;
  const coef = r.unit_coefficient;
  const loose = coef > 1 && draft.qty % coef !== 0;
  const isExtra = draft.mode === "extra";

  // Живой прогноз по заказу для выбранной (ном+характеристика+партия+единица).
  const charKey = draft.withoutChar ? "" : draft.charRef;
  const partiaKey = draft.withoutPartia ? "" : draft.partiaRef;
  // findNeed строит индекс потребностей — мемоизируем, чтобы не пересобирать на
  // каждый ре-рендер шторки (ввод партии / степпер количества).
  const need = useMemo(
    () => findNeed(needs, r.nomenclature_ref, charKey, partiaKey, coef),
    [needs, r.nomenclature_ref, charKey, partiaKey, coef],
  );
  const remaining = need ? Math.max(0, need.qty_needed - need.qty_scanned) : 0;
  const afterAdd = (need?.qty_scanned ?? 0) + draft.qty;
  const overE = need ? Math.max(0, afterAdd - need.qty_needed) : 0;
  const isOverMode = !needChar && !isExtra && need != null && afterAdd > need.qty_needed + REMAINING_EPS;
  // Коробовая потребность заказа: россыпь запрещена, количества — в коробах.
  const isBoxNeed = !!need && need.unit_coef > 1;
  const nCoef = isBoxNeed ? need!.unit_coef : 1;
  const nUnit = isBoxNeed ? "кор." : "шт";
  const showN = (base: number): string => fmtQty(isBoxNeed ? base / nCoef : base);

  const canAdd = !needChar && partiaReady && draft.qty > 0;
  const reason = needChar ? "Выберите характеристику" : !partiaReady ? "Укажите партию или «без партии»" : draft.qty <= 0 ? "Укажите количество" : "";

  return (
    <BottomSheet onClose={onCancel} panelClassName="max-h-[85%] overflow-y-auto">
        <div className={cn("flex items-start gap-2 p-3", isExtra ? "bg-red-50" : draft.mode === "over" ? "bg-amber-50" : "bg-white")}>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <span className="font-mono text-[18px] font-bold">{r.nomenclature_article}</span>
              {coef > 1 && <span className="rounded border border-sky-200 bg-sky-100 px-1.5 py-0.5 text-[10px] font-bold text-sky-800">короб ×{fmtQty(coef)}</span>}
            </div>
            <div className="mt-0.5 text-[16px] font-bold">
              {needChar ? "Выберите характеристику" : draft.mode === "partia" ? "Укажите партию" : draft.mode === "over" ? "Перебор — выберите количество" : isExtra ? "Нет в заказе — лишняя позиция" : "Уточните скан"}
            </div>
            <div className="text-[11px] text-slate-500 line-clamp-1">{r.nomenclature_name}</div>
          </div>
          <button type="button" onClick={onCancel} className="flex size-8 items-center justify-center">
            <X className="size-4" />
          </button>
        </div>

        <div className="space-y-3 p-3">
          {/* Живой прогноз по заказу */}
          {need ? (
            <div
              className={cn(
                "rounded-lg border p-2 text-[12px]",
                overE > 0 ? "border-red-200 bg-red-50 text-red-800" : afterAdd >= need.qty_needed - REMAINING_EPS ? "border-emerald-200 bg-emerald-50 text-emerald-800" : "border-sky-200 bg-sky-50 text-sky-800",
              )}
            >
              <div>
                По заказу: нужно <b>{showN(need.qty_needed)}</b> · собрано <b>{showN(need.qty_scanned)}</b> · осталось <b>{showN(remaining)}</b> {nUnit}
              </div>
              <div className="mt-0.5 font-semibold">
                {overE > 0
                  ? `После добавления: ${showN(afterAdd)} — перебор +${showN(overE)} ${nUnit}`
                  : Math.abs(need.qty_needed - afterAdd) <= REMAINING_EPS
                    ? `После добавления позиция закроется точно (${showN(afterAdd)} ${nUnit})`
                    : `После добавления останется ${showN(need.qty_needed - afterAdd)} ${nUnit}`}
              </div>
            </div>
          ) : isExtra ? (
            <div className="rounded-lg border border-red-200 bg-red-50 p-2 text-[12px] text-red-800">
              Позиция заблокирует завершение, пока её не удалят.
            </div>
          ) : null}

          {/* Характеристика — общий выбор (`lib/scan-ui`): он разворачивает
              серверный вариант «без характеристики» в пустую ссылку.
              ⚠️ Блок виден и ПОСЛЕ ответа, с подсветкой выбранного. Пряча его,
              я сделал ответ необратимым: «Без характеристики» — первая кнопка,
              промах по ней закрывал блок насовсем, и позиция уходила в документ
              1С с пустой характеристикой. Отменить внутри шторки было нечем. */}
          <CharacteristicChoice
            options={r.available_characteristics}
            answer={{ charRef: draft.charRef, charName: draft.charName, withoutChar: draft.withoutChar }}
            onPick={(c) => setDraft({ ...draft, ...charAnswer(c) })}
            required={needChar}
          />

          {/* Партия */}
          {(draft.mode === "partia" || r.needs_partia) && (
            <PartiaAsk
              value={partiaInput}
              onChange={setPartiaInput}
              hasPicked={!!draft.partiaRef}
              picked={draft.partiaName}
              onReset={() =>
                setDraft({ ...draft, partiaRef: "", partiaName: "", partiaSource: "" })
              }
              onLookup={(name) => partiaM.mutate(name)}
              busy={partiaM.isPending}
              withoutPartia={draft.withoutPartia}
              onWithoutPartia={(v) => setDraft({ ...draft, withoutPartia: v })}
            />
          )}

          {/* Перебор / добор — две взаимоисключающие карты */}
          {isOverMode && !needChar && partiaReady && (
            <div className="space-y-2">
              {/* Россыпь запрещена для коробовых строк — только для базовых;
                  для кода с упаковки — подсказка вместо кнопки (`LooseRemainder`). */}
              {!isBoxNeed && remaining > REMAINING_EPS ? (
                <LooseRemainder
                  fromPacking={r.source === "packing"}
                  remaining={remaining}
                  onTake={() => onConfirm(true, remaining)}
                />
              ) : isBoxNeed ? (
                <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-[12px] text-amber-800">
                  Строка заказа — в коробах (×{fmtQty(nCoef)}). Россыпь недопустима: собирайте целыми коробами.
                </div>
              ) : (
                <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 text-[12px] text-slate-600">
                  Позиция уже собрана полностью — {showN(need?.qty_scanned ?? 0)}/{showN(need?.qty_needed ?? 0)} {nUnit}. Остатка нет.
                </div>
              )}
              <button
                type="button"
                onClick={() => onConfirm(loose, draft.qty)}
                className="w-full rounded-xl border-2 border-red-300 bg-red-50 p-3 text-left"
              >
                <div className="text-[15px] font-bold text-red-800">Принять полностью — {showN(draft.qty)} {nUnit} (перебор +{showN(overE)})</div>
                <div className="text-[11px] text-red-700">Перебор заблокирует завершение</div>
              </button>
            </div>
          )}

          {/* Количество */}
          <div className="rounded-lg border p-2">
            <div className="mb-1.5 text-[12px] font-semibold text-slate-500">Количество, шт:</div>
            <div className="flex items-center justify-center gap-3">
              <button type="button" className="size-12 rounded-lg border text-[22px]" onClick={() => setDraft({ ...draft, qty: Math.max(0, draft.qty - 1) })}>−</button>
              <QtyInput value={draft.qty} onChange={(v) => setDraft({ ...draft, qty: v })} />
              <button type="button" className="size-12 rounded-lg border text-[22px]" onClick={() => setDraft({ ...draft, qty: draft.qty + 1 })}>+</button>
            </div>
            <div className="mt-1 text-center text-[11px] text-slate-500">
              {isBoxNeed && loose
                ? `частичный короб недопустим — нужны целые короба ×${fmtQty(nCoef)}`
                : coef > 1
                  ? loose
                    ? "частичный короб — россыпь (коробов 0)"
                    : `= ${fmtQty(draft.qty / coef)} кор.`
                  : "штуки"}
            </div>
          </div>
        </div>

        <div className="flex gap-2 border-t p-3">
          <Button variant="outline" className="h-[52px] flex-[0.4]" onClick={onCancel}>
            Отмена
          </Button>
          {!isOverMode && (
            <Button
              className={cn("h-[52px] flex-1", isExtra ? "bg-red-700 hover:bg-red-800" : "bg-emerald-600 hover:bg-emerald-700")}
              disabled={!canAdd}
              onClick={() => onConfirm(loose, draft.qty)}
            >
              <Check className="size-4" /> Добавить {fmtQty(draft.qty)} шт
            </Button>
          )}
          {isOverMode && (
            <div className="flex-1 self-center text-center text-[11px] font-semibold text-amber-600">Выберите вариант выше</div>
          )}
        </div>
        {reason && <div className="pb-3 text-center text-[11px] font-semibold text-amber-600">{reason}</div>}
    </BottomSheet>
  );
}

// ─────────────────────────── SH2 ──────────────────────────────────────────
type PosFilter = "remain" | "problem" | "done" | "all";

//: Какая шторка открыта. Расхождение плана с заказом живёт ЗДЕСЬ, а не
//: полосой в разметке: у крупного заказа таких позиций полсотни, и список
//: заслонял весь экран — героя скана было не видно вовсе.
type SheetState =
  | null
  | { kind: "positions"; filter: PosFilter }
  | { kind: "pallet" }
  | { kind: "plan-check" };
function PositionsSheet({
  detail,
  filter,
  onClose,
  onDeleteItem,
  onDeleteItems,
  deletingItems = false,
}: {
  detail: SessionDetail;
  filter: PosFilter;
  onClose: () => void;
  onDeleteItem?: (id: string) => void;
  /** Массовое удаление — одним вызовом: порядок и пропуск снятых сервером
   *  строк решает страница (`deleteItemsInOrder`), а не цикл здесь. */
  onDeleteItems?: (ids: string[]) => void;
  deletingItems?: boolean;
}): React.ReactElement {
  const [f, setF] = useState<PosFilter>(filter);
  const [expanded, setExpanded] = useState<string | null>(null);
  // Гранулярность заказа — (ном, хар, ПАРТИЯ, ЕДИНИЦА). Позиция «не из заказа» =
  // скан, чей ключ (эффективная партия+единица) не совпал ни с одной строкой.
  const idx = useMemo(() => buildNeedIndex(detail.needs), [detail.needs]);
  const needKeys = useMemo(
    () => new Set(detail.needs.map((n) => needGranKey(n))),
    [detail.needs],
  );
  const extraItemsByKey = useMemo(() => {
    const m: Record<string, { id: string; palletLabel: string; qty: number; boxes: number }[]> = {};
    for (const p of detail.pallets)
      for (const it of p.items) {
        const key = granKey(it.nomenclature_ref ?? "", it.characteristic_ref ?? "", it.partia_ref ?? "", it.unit_coefficient ?? 1, idx);
        if (!needKeys.has(key)) (m[key] ??= []).push({ id: it.id, palletLabel: p.label ?? "", qty: it.qty_base, boxes: it.boxes });
      }
    return m;
  }, [detail.pallets, needKeys, idx]);
  const needs = [...detail.needs].sort(compareNeedsForSheet);
  const shown = needs.filter((n) => {
    const st = needStatus(n);
    if (f === "remain") return st === "partial" || st === "idle";
    if (f === "problem") return st === "over";
    if (f === "done") return st === "done";
    return true;
  });
  const counts = {
    remain: needs.filter((n) => ["partial", "idle"].includes(needStatus(n))).length,
    problem: needs.filter((n) => needStatus(n) === "over").length + detail.extras.length,
    done: needs.filter((n) => needStatus(n) === "done").length,
  };
  return (
    <BottomSheet onClose={onClose} panelClassName="flex max-h-[90%] flex-col">
        <div className="flex items-center justify-between border-b p-3">
          <div className="text-[15px] font-bold">Позиции заказа</div>
          <div className="flex items-center gap-2">
            <span className="text-[12px] text-slate-500">{fmtQty(detail.qty_scanned_total)} / {fmtQty(detail.qty_needed_total)} шт</span>
            <button type="button" onClick={onClose} className="flex size-8 items-center justify-center"><X className="size-4" /></button>
          </div>
        </div>
        <div className="flex gap-1.5 border-b p-2">
          {([["remain", `Осталось (${counts.remain})`], ["problem", `Проблемы (${counts.problem})`], ["done", `Собрано (${counts.done})`], ["all", "Все"]] as const).map(([k, l]) => (
            <button key={k} type="button" onClick={() => setF(k)} className={cn("h-9 flex-1 rounded-md text-[12px] font-semibold", f === k ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-600")}>
              {l}
            </button>
          ))}
        </div>
        <div className="flex-1 space-y-1.5 overflow-y-auto p-2">
          {shown.map((n) => {
            const st = needStatus(n);
            const box = n.unit_coef > 1;
            const sN = (base: number): string => fmtQty(box ? base / n.unit_coef : base);
            const u = box ? "кор." : "шт";
            return (
              <GoodsCard
                key={`${n.nomenclature_ref}|${n.characteristic_ref}|${n.partia_ref}|${n.unit_coef}`}
                status={st}
                article={n.nomenclature_article}
                name={n.nomenclature_name}
                characteristic={n.characteristic_name}
                characteristicRef={n.characteristic_ref}
                partia={n.partia_name}
                boxCoef={n.unit_coef}
                note={
                  st === "done"
                    ? "✓ собрано точно"
                    : st === "over"
                      ? `+${sN(n.qty_scanned - n.qty_needed)} ${u} лишних`
                      : `осталось ${sN(Math.max(0, n.qty_needed - n.qty_scanned))} ${u}`
                }
                scanned={box ? n.qty_scanned / n.unit_coef : n.qty_scanned}
                needed={box ? n.qty_needed / n.unit_coef : n.qty_needed}
                unit={u}
              />
            );
          })}
          {f !== "done" && detail.extras.length > 0 && (
            <div className="rounded-lg border border-red-200 bg-red-50 p-2">
              <div className="mb-1 flex items-center justify-between">
                <div className="text-[13px] font-bold text-red-700">Не из заказа ({detail.extras.length})</div>
                {onDeleteItems && (
                  <button
                    type="button"
                    disabled={deletingItems}
                    onClick={() => onDeleteItems(Object.values(extraItemsByKey).flat().map((it) => it.id))}
                    className="rounded bg-red-700 px-2 py-1 text-[11px] font-bold text-white disabled:opacity-50"
                  >
                    Удалить все
                  </button>
                )}
              </div>
              {detail.extras.map((e) => {
                const key = `${e.nomenclature_ref}|${norm(e.characteristic_ref)}|${norm(e.partia_ref)}|${ck(e.unit_coef)}`;
                const items = extraItemsByKey[key] ?? [];
                const open = expanded === key;
                const wrongUnit = requiredBoxCoef(detail.needs, e.nomenclature_ref, e.characteristic_ref ?? "") > 1;
                return (
                  <div key={key} className="mt-1 rounded border border-red-200 bg-white p-1.5">
                    <button type="button" onClick={() => setExpanded(open ? null : key)} className="flex w-full items-center justify-between text-left text-[11px]">
                      <span className="min-w-0 truncate text-red-800">{e.nomenclature_name} {e.characteristic_name}{e.partia_name ? ` · парт. ${e.partia_name}` : ""}{wrongUnit ? " · не та единица (нужны короба)" : e.unit_coef > 1 ? ` · короб ×${fmtQty(e.unit_coef)}` : ""}</span>
                      <span className="ml-2 shrink-0 font-bold text-red-700">{fmtQty(e.qty_scanned)} шт {onDeleteItem ? (open ? "▲" : "▼") : ""}</span>
                    </button>
                    {open && onDeleteItem && (
                      <div className="mt-1 space-y-1">
                        {items.map((it) => (
                          <div key={it.id} className="flex items-center justify-between rounded bg-red-50 px-1.5 py-1 text-[11px]">
                            <span>{it.palletLabel} · {fmtQty(it.qty)} шт{it.boxes > 0 ? ` · ${fmtQty(it.boxes)} кор` : ""}</span>
                            <button type="button" onClick={() => onDeleteItem(it.id)} className="rounded bg-red-700 px-2 py-0.5 text-[10px] font-bold text-white">
                              Удалить
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
          {shown.length === 0 && detail.extras.length === 0 && <div className="py-6 text-center text-[13px] text-slate-400">Нет позиций в этой группе</div>}
        </div>
    </BottomSheet>
  );
}

// ─────────────────────────── S4 completed ─────────────────────────────────
function CompletedScreen({
  detail,
  photoCount,
  onReopen,
  onClear,
  onOpenPositions,
  onBack,
  dialog,
  onCloseDialog,
  onConfirmReopen,
  onConfirmClear,
  reopenBusy,
  sheet,
  onCloseSheet,
}: {
  detail: SessionDetail;
  photoCount: number;
  onReopen: () => void;
  onClear: () => void;
  onOpenPositions: () => void;
  onBack: () => void;
  dialog: null | "fin" | "reopen" | "clear";
  onCloseDialog: () => void;
  onConfirmReopen: () => void;
  onConfirmClear: () => void;
  reopenBusy: boolean;
  sheet: SheetState;
  onCloseSheet: () => void;
}): React.ReactElement {
  const h = detail.header;
  const filled = detail.pallets.filter((p) => p.items_count > 0);
  const [busy, setBusy] = useState(false);
  const dl = async (fn: () => Promise<void>, fallback: string) => {
    setBusy(true);
    try {
      await fn();
    } catch (e) {
      toast.error(await blobErrorMessage(e, fallback));
    } finally {
      setBusy(false);
    }
  };
  return (
    <>
      <AppBar detail={detail} chip="paused" onBack={onBack} />
      <div className="flex-1 space-y-3 overflow-y-auto p-3">
        <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3">
          <div className="flex items-center gap-2 text-[16px] font-bold text-emerald-700">
            <CheckCircle2 className="size-5" /> Проверка завершена
          </div>
          {h.expense_number && <div className="mt-1 text-[13px] text-emerald-900">Расходная накладная: <b>{h.expense_number}</b> (без проведения)</div>}
          {photoCount > 0 && <div className="text-[12px] text-emerald-800">Фото паллет отправлены в заказ 1С: {photoCount} шт.</div>}
        </div>
        <button type="button" onClick={onOpenPositions} className="flex w-full items-center justify-between rounded-lg border bg-white p-3 text-left">
          <span className="text-[13px] font-semibold">Собрано {fmtQty(detail.qty_scanned_total)} / {fmtQty(detail.qty_needed_total)} шт</span>
          <ChevronRight className="size-4 text-slate-300" />
        </button>
        <div className="space-y-1.5">
          <div className="text-[12px] font-semibold text-slate-500">Паллеты ({filled.length})</div>
          {filled.map((p) => (
            <div key={p.id} className="rounded-lg border bg-white p-2.5 text-[13px]">
              <b>{p.label}</b>{palletNumber(p) ? ` · ${palletNumber(p)}` : ""} — {p.items_count} поз · {fmtQty(p.qty)} шт{p.boxes > 0 ? ` · ${fmtQty(p.boxes)} кор` : ""}
            </div>
          ))}
        </div>
        {/* Этикетки печатаем ЗДЕСЬ, а не при открытии паллеты: только теперь
            известно, сколько всего паллет, и на этикетку попадает «N из M» —
            по нему приёмщик пересчитывает места. */}
        {filled.length > 0 && (
          <Button
            className="h-[52px] w-full"
            disabled={busy}
            onClick={() =>
              dl(async () => {
                const r = await printAllPalletLabels(detail.header.id);
                toast.success(`Напечатано этикеток: ${r.printed} из ${r.total}`);
              }, "Не удалось напечатать этикетки")
            }
          >
            <Printer className="size-4" /> Этикетки паллет ({filled.length})
          </Button>
        )}
        <Button className="h-[52px] w-full bg-sky-700 hover:bg-sky-800" disabled={busy} onClick={() => dl(() => downloadAllPalletSheets(detail.header.id), "Не удалось скачать листы")}>
          <FileText className="size-4" /> Паллетные листы (DOCX)
        </Button>
        {/* Пересохранение в 1С: лист собрался не так (EDI опоздал) или поменяли
            правила — вложение в заказе заменяется, старые наши помечаются на
            удаление. Скачивание выше 1С не трогает. */}
        <Button
          variant="outline"
          className="h-12 w-full"
          disabled={busy}
          onClick={() =>
            dl(async () => {
              const r = await reattachPalletSheets(detail.header.id);
              toast.success(
                `Лист «${r.filename}» пересобран и прикреплён к заказу в 1С (прежний помечен на удаление)`,
              );
            }, "Не удалось пересохранить лист в 1С")
          }
        >
          <FileText className="size-4" /> Пересохранить лист в 1С
        </Button>
        {h.network === "zolotoe_yabloko" && (
          <Button variant="outline" className="h-[52px] w-full" disabled={busy} onClick={() => dl(() => downloadZyaReestr(detail.header.id), "Не удалось скачать реестр")}>
            <FileText className="size-4" /> Реестр отгрузки (XLSX)
          </Button>
        )}
        {/* «Новой проверки этого заказа» нет (убрана 27.10.2026): её завершение
            создавало в 1С вторую расходную. Ошибки исправляют «Вернуть в работу». */}
        <Button variant="outline" className="h-12 w-full" onClick={onReopen}>
          <RotateCcw className="size-4" /> Вернуть в работу
        </Button>
        <button type="button" onClick={onClear} className="w-full text-center text-[12px] text-red-600">
          Ещё… → Очистить заказ полностью
        </button>
      </div>

      {sheet?.kind === "positions" && <PositionsSheet detail={detail} filter={sheet.filter} onClose={onCloseSheet} />}
      {dialog === "reopen" && (
        <Dialog
          title="Вернуть в работу?"
          body="Документы в 1С будут распроведены и помечены на удаление. Можно будет править сборку."
          confirmLabel="Вернуть"
          confirmBusy={reopenBusy}
          onConfirm={onConfirmReopen}
          onCancel={onCloseDialog}
        />
      )}
      {dialog === "clear" && (
        <Dialog
          title="Очистить заказ полностью?"
          body="Все сканы будут удалены, документы распроведены и помечены на удаление."
          confirmLabel="Очистить"
          confirmBusy={reopenBusy}
          danger
          onConfirm={onConfirmClear}
          onCancel={onCloseDialog}
        />
      )}
    </>
  );
}
